import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { handleCartAddFailure, showInlineCartError } from '@theme/cart-add-feedback';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion, fetchConfig, getCartItemsSectionIds, cartSectionsUrl } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { transplantQuickAddPowerReviewsPlaceholder } from '@theme/morph';

/**
 * @param {ParentNode} html
 * @returns {Element | null}
 */
const extractQuickAddProductGrid = (html) => {
  const grid =
    html.querySelector('[data-product-grid-content]') ||
    html.querySelector('.product-information__grid') ||
    html.querySelector('.product-information-v2__grid');
  if (!grid) return null;

  // The fetched page carries the Constructor item attributes on the
  // [data-cnstrc-product-detail] wrapper OUTSIDE this grid, so extracting the
  // grid alone strips them and the beacon's add_to_cart event from the quick
  // shop loses item-id/variation-id/price. Copy them onto the grid — an
  // ancestor of the add-to-cart button ([data-cnstrc-btn="add_to_cart"]).
  const cnstrcDetail = html.querySelector('[data-cnstrc-product-detail]');
  if (cnstrcDetail) {
    for (const attr of [
      'data-cnstrc-item-id',
      'data-cnstrc-item-name',
      'data-cnstrc-item-variation-id',
      'data-cnstrc-item-price',
    ]) {
      const value = cnstrcDetail.getAttribute(attr);
      if (value !== null) grid.setAttribute(attr, value);
    }
  }
  return grid;
};

/**
 * Shared quick-add render cache. Every surface that can warm or open a quick
 * shop reads and writes these two maps: the hover prefetch on Liquid
 * `<product-card>`s, the `bonobos:prefetch-quick-add` event the React PLP card
 * fires, `QuickAddComponent#handleClick`, and the module-level
 * `openQuickAddDialog`. Before this was shared, `openQuickAddDialog` issued a
 * cold `fetch()` on every open and never saw the prefetch — which is why quick
 * shop off the React PLP (no prefetch at all) felt much slower than off the
 * homepage carousel.
 * @type {Map<string, Element>}
 */
const quickAddGridCache = new Map();
/** @type {Map<string, Promise<Element | null>>} */
const quickAddGridInflight = new Map();
// Bumped on every cache clear. A fetch captures the generation at kickoff and
// only publishes its result if it still matches — otherwise a request already
// in flight when the cart updates would write now-stale, cart-dependent HTML
// back into the freshly cleared cache.
let quickAddCacheGeneration = 0;

/** Params that don't change what the quick-add view renders. */
const QUICK_ADD_IGNORED_PARAMS = [
  'breadcrumb_title',
  'breadcrumb_url',
  'breadcrumb_child_title',
  'breadcrumb_child_url',
  'breadcrumb_current_title',
];

/**
 * Cache key for a product URL. Strips the params that don't affect the render;
 * `color` and `variant` stay in the key because they do.
 * @param {string} productPageUrl
 * @returns {string}
 */
function getQuickAddUrlKey(productPageUrl) {
  try {
    const url = new URL(productPageUrl, window.location.origin);
    QUICK_ADD_IGNORED_PARAMS.forEach((param) => url.searchParams.delete(param));
    url.searchParams.delete('view');
    url.searchParams.delete('quick_add');
    return url.pathname + url.search;
  } catch {
    return productPageUrl;
  }
}

/**
 * @param {string} productPageUrl
 * @returns {string}
 */
function buildQuickAddRequestUrl(productPageUrl) {
  const url = new URL(productPageUrl, window.location.origin);
  QUICK_ADD_IGNORED_PARAMS.forEach((param) => url.searchParams.delete(param));
  url.searchParams.set('view', 'quick-add');
  url.searchParams.set('quick_add', '1');
  return url.toString();
}

/**
 * Fetch the quick-add product grid for a URL, reusing a cached or in-flight
 * render when one exists. Always resolves to a detached clone the caller owns
 * (or null) — the cached original is never handed out. Never rejects, so a
 * failed prefetch can't surface as an unhandled rejection; callers treat null
 * as "fall back to navigating to the PDP".
 * @param {string} productPageUrl
 * @returns {Promise<Element | null>}
 */
async function fetchQuickAddGrid(productPageUrl) {
  if (!productPageUrl) return null;

  const key = getQuickAddUrlKey(productPageUrl);
  const cached = quickAddGridCache.get(key);
  if (cached) {
    return /** @type {Element} */ (cached.cloneNode(true));
  }

  const existing = quickAddGridInflight.get(key);
  if (existing) {
    const grid = await existing;
    return grid ? /** @type {Element} */ (grid.cloneNode(true)) : null;
  }

  const generation = quickAddCacheGeneration;
  const promise = (async () => {
    const requestUrl = buildQuickAddRequestUrl(productPageUrl);
    try {
      const response = await fetch(requestUrl);
      if (!response.ok) throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const gridElement = extractQuickAddProductGrid(html);
      if (!gridElement) {
        console.warn('[QuickAdd] no product grid in fetched HTML for', requestUrl);
        return null;
      }

      const grid = /** @type {Element} */ (gridElement.cloneNode(true));
      // Drop the result if the cache was cleared while this was in flight — the
      // render may reflect a pre-cart-update state.
      if (generation === quickAddCacheGeneration) {
        quickAddGridCache.set(key, grid);
      }
      return grid;
    } catch (error) {
      console.error('[QuickAdd] fetch error:', error);
      return null;
    }
  })();

  quickAddGridInflight.set(key, promise);
  try {
    const grid = await promise;
    return grid ? /** @type {Element} */ (grid.cloneNode(true)) : null;
  } finally {
    quickAddGridInflight.delete(key);
  }
}

/**
 * Kick the variant availability fetch for a quick-add grid we've already
 * rendered but not yet mounted.
 *
 * This is the second of the two serial round trips a quick view pays. Left
 * alone it can't start until the HTML is in the live DOM, so it lands squarely
 * on the critical path after the click. Starting it from the prefetched
 * document overlaps it with the shopper's hover instead — and because
 * VariantPickerV2 caches by product, the picker's own fetch on mount then hits
 * cache. Both trips scale with variant count, which is why heavy combined
 * listings hurt most.
 *
 * @param {Element | null} grid
 */
async function warmQuickAddAvailability(grid) {
  const picker = grid?.querySelector?.('variant-picker-v2');
  if (!(picker instanceof Element)) return;

  let ctor = /** @type {any} */ (customElements.get('variant-picker-v2'));
  if (typeof ctor?.warmAvailability !== 'function') {
    // On a fresh load the hover prefetch can fire before variant-picker-v2.js
    // has registered the element. Wait briefly for it rather than silently
    // skipping the warm — but never hang this fire-and-forget path, so race the
    // definition against a short timeout.
    await Promise.race([
      customElements.whenDefined('variant-picker-v2'),
      new Promise((resolve) => setTimeout(resolve, 2000)),
    ]);
    ctor = /** @type {any} */ (customElements.get('variant-picker-v2'));
    if (typeof ctor?.warmAvailability !== 'function') return;
  }

  void ctor.warmAvailability({
    endpoint: picker.getAttribute('data-endpoint') || undefined,
    productId: picker.getAttribute('data-product-id') || undefined,
    parentProductId: picker.getAttribute('data-parent-product-id') || undefined,
    storefrontToken: picker.getAttribute('data-storefront-token') || undefined,
    clRole: picker.getAttribute('data-cl-role') || undefined,
    // A combined-listing rail Liquid already grouped from the parent colour
    // metafields needs no BFF bundle — same marker variant-picker-v2.js keys on.
    ssrColorGroups: Boolean(
      picker.querySelector('[data-piv2-color-groups][data-piv2-color-groups-source="metafield"]')
    ),
    // Same Sale-by-price mode the picker will request on mount, so the warm
    // fills the entry it reads (URL and cache key both carry the mode).
    saleGrouping: picker.getAttribute('data-sale-grouping') || undefined,
  });
}

/**
 * Warm the shared cache without opening anything. Fire-and-forget.
 * @param {string} productPageUrl
 */
function prefetchQuickAddGrid(productPageUrl) {
  if (!productPageUrl) return;
  void fetchQuickAddGrid(productPageUrl).then(warmQuickAddAvailability);
}

/** Drop every cached render — the grid embeds cart-dependent state. */
function clearQuickAddGridCache() {
  quickAddCacheGeneration += 1;
  quickAddGridCache.clear();
  quickAddGridInflight.clear();
}

// Invalidate at the document level, not just from QuickAddComponent — the React
// PLP renders no <quick-add-component>, so the instance-level handler never runs
// there and the cache would otherwise go stale after an add-to-cart.
document.addEventListener(ThemeEvents.cartUpdate, clearQuickAddGridCache);

// The React PLP card can't reach into this module (separate Vite bundle), so it
// warms the cache by event — same pattern as `bonobos:open-quick-add`.
document.addEventListener('bonobos:prefetch-quick-add', (event) => {
  const detail = event instanceof CustomEvent ? event.detail : null;
  const productUrl = detail?.productUrl;
  if (typeof productUrl !== 'string' || !productUrl) return;
  prefetchQuickAddGrid(productUrl);
});

/**
 * @param {string} value
 */
const normalizeQuickAddOptionValue = (value = '') =>
  value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getActiveQuickAddVariantPicker = (root) => {
  if (!(root instanceof Element)) return null;

  const desktopPicker = root.querySelector('.product-information__details-desktop variant-picker');
  if (desktopPicker instanceof VariantPicker) return desktopPicker;

  const visiblePicker = Array.from(root.querySelectorAll('variant-picker')).find((picker) => {
    if (!(picker instanceof HTMLElement)) return false;
    if (picker.closest('[hidden],[inert]')) return false;
    return picker.getClientRects().length > 0;
  });

  return visiblePicker instanceof VariantPicker ? visiblePicker : null;
};

const clearQuickAddImplicitNonColorSelections = (root) => {
  const picker = getActiveQuickAddVariantPicker(root) || root?.querySelector?.('variant-picker');
  if (!(picker instanceof HTMLElement)) return;

  if (picker instanceof VariantPicker && typeof picker.clearImplicitNonColorSelectionsForQuickAdd === 'function') {
    picker.clearImplicitNonColorSelectionsForQuickAdd();
    requestAnimationFrame(() => picker.clearImplicitNonColorSelectionsForQuickAdd());
    return;
  }

  picker.querySelectorAll('fieldset[data-option-name-handle]').forEach((fieldset) => {
    if (!(fieldset instanceof HTMLFieldSetElement)) return;
    const handle = fieldset.dataset.optionNameHandle || '';
    if (handle === 'color' || handle === 'colour') return;

    fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.checked = false;
      input.dataset.currentChecked = 'false';
      input.removeAttribute('checked');
    });

    const selectedValueLabel = fieldset.querySelector('[data-variant-selected-value]');
    if (selectedValueLabel instanceof HTMLElement) {
      const placeholder =
        selectedValueLabel.dataset.variantPlaceholder ||
        selectedValueLabel.dataset.variantError ||
        '';
      selectedValueLabel.textContent = placeholder;
      selectedValueLabel.classList.remove('variant-option__selected-value', 'is-visible');
      if (placeholder) selectedValueLabel.classList.add('variant-option__error');
    }
  });

  picker.querySelectorAll('select[data-option-name-handle], select.variant-option__select[data-option-name-handle]').forEach((select) => {
    if (!(select instanceof HTMLSelectElement)) return;
    const handle = select.dataset.optionNameHandle || '';
    if (handle === 'color' || handle === 'colour') return;
    select.selectedIndex = -1;
    Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
  });
};

const scheduleQuickAddImplicitNonColorSelectionClear = (root) => {
  if (!(root instanceof Element)) return;

  clearQuickAddImplicitNonColorSelections(root);
  queueMicrotask(() => clearQuickAddImplicitNonColorSelections(root));
  requestAnimationFrame(() => {
    clearQuickAddImplicitNonColorSelections(root);
    requestAnimationFrame(() => clearQuickAddImplicitNonColorSelections(root));
  });
  window.setTimeout(() => clearQuickAddImplicitNonColorSelections(root), 80);
  window.setTimeout(() => clearQuickAddImplicitNonColorSelections(root), 250);
};

const resolveQuickAddPowerReviewsPageId = (root) => {
  if (!(root instanceof Element)) return '';
  const explicitProductFormId = root.querySelector('product-form-component[data-product-id]')?.getAttribute('data-product-id');
  if (explicitProductFormId) return explicitProductFormId;

  const explicitNodeId = root.querySelector('[data-product-id]')?.getAttribute('data-product-id');
  if (explicitNodeId) return explicitNodeId;

  const quickAddHostId = root.closest('[data-product-id]')?.getAttribute('data-product-id');
  return quickAddHostId || '';
};

/**
 * Converts quick-add PowerReviews placeholders into CategorySnippet nodes and refreshes stars.
 * @param {Element} root
 */
const hydrateQuickAddPowerReviewsSnippets = (root) => {
  if (!(root instanceof Element)) return;

  const placeholders = root.querySelectorAll('.quick-add-product-details [power-reviews-placeholder]');
  if (!placeholders.length) return;
  const quickAddPageId = resolveQuickAddPowerReviewsPageId(root);

  let needsRefresh = false;
  placeholders.forEach((placeholder) => {
    if (!(placeholder instanceof HTMLElement)) return;
    if (placeholder.dataset.prMounted === 'true' || placeholder.childNodes.length > 0) {
      return;
    }
    placeholder.setAttribute('data-pr-component', 'CategorySnippet');
    if (!placeholder.dataset.prPageId) {
      if (quickAddPageId) {
        placeholder.setAttribute('data-pr-page-id', quickAddPageId);
      }
    }
    needsRefresh = true;
  });

  if (!needsRefresh) return;

  const refreshSnippets = window.refreshPowerReviewsCategorySnippets;
  if (typeof refreshSnippets === 'function') {
    refreshSnippets(root);
  }
};

/**
 * Carry a `color`/`colour` query param from a source URL onto a freshly built
 * pathname-only URL. The productPageUrl getter strips all query params in
 * quick-add mode and rebuilds them from the variant-picker state — but cards
 * without their own picker (constructor-rendered recommendation tiles, cart
 * carousel) encode the user's swatch choice in the URL itself. Without this,
 * the modal's #syncColorSelection never sees the requested color and the
 * default variant wins.
 * @param {URL} source - URL providing the param.
 * @param {URL} target - URL to copy onto.
 */
function preserveColorParam(source, target) {
  if (!(source instanceof URL) || !(target instanceof URL)) return;
  const color = source.searchParams.get('color') || source.searchParams.get('colour');
  if (color && !target.searchParams.has('color') && !target.searchParams.has('colour')) {
    target.searchParams.set('color', color);
  }
}

function getSelectedQuickAddColorValue(root) {
  if (!(root instanceof Element)) return '';

  const colorFieldset = root.querySelector(
    'variant-picker fieldset[data-option-name-handle="color"], variant-picker fieldset[data-option-name-handle="colour"]'
  );
  if (colorFieldset instanceof HTMLFieldSetElement) {
    const checkedInput =
      colorFieldset.querySelector('input[type="radio"]:checked') ||
      colorFieldset.querySelector('input[type="radio"][data-current-checked="true"]');

    if (checkedInput instanceof HTMLInputElement) {
      return checkedInput.value?.trim() || checkedInput.getAttribute('aria-label')?.trim() || '';
    }
  }

  const colorSelect = root.querySelector(
    'variant-picker select[data-option-name-handle="color"], variant-picker select[data-option-name-handle="colour"]'
  );
  if (colorSelect instanceof HTMLSelectElement) return colorSelect.value?.trim() || '';

  const fallbackInput = root.querySelector('input[name*=Color]:checked, input[name*=Colour]:checked');
  if (fallbackInput instanceof HTMLInputElement) return fallbackInput.value?.trim() || '';

  return '';
}

/**
 * @param {URL} url
 * @returns {URL}
 */
function ensureVariantFirstQueryParam(url) {
  if (!url.searchParams.has('variant')) return url;

  const variantValue = url.searchParams.get('variant');
  const ordered = new URLSearchParams();
  if (variantValue) ordered.set('variant', variantValue);
  url.searchParams.delete('variant');
  url.searchParams.forEach((value, key) => ordered.append(key, value));
  url.search = ordered.toString();
  return url;
}

/**
 * @param {Element | null | undefined} root
 * @returns {Element | null}
 */
function getQuickAddContentRoot(root) {
  if (root instanceof HTMLElement && root.id === 'quick-add-modal-content') return root;
  if (!(root instanceof Element)) return null;
  return root.querySelector('#quick-add-modal-content') || root;
}

/**
 * @param {Element | null | undefined} root
 * @param {URL} url
 * @returns {URL | null}
 */
function buildPiv2QuickAddViewProductUrl(root, url) {
  const scope = getQuickAddContentRoot(root);
  if (!(scope instanceof Element)) return null;

  const picker = scope.querySelector('variant-picker-v2');
  if (!(picker instanceof HTMLElement)) return null;

  let nextUrl = url;
  const selectedColorBtn = picker.querySelector(
    '.piv2-option-group[data-option-handle="color"] .piv2-swatch-btn.selected, .piv2-option-group[data-option-handle="colour"] .piv2-swatch-btn.selected'
  );
  const connectedUrl =
    selectedColorBtn instanceof HTMLElement ? selectedColorBtn.dataset.connectedProductUrl?.trim() : '';
  const pickerProductUrl = picker.dataset.productUrl?.trim() || '';

  if (connectedUrl) {
    try {
      nextUrl = new URL(connectedUrl, window.location.origin);
    } catch {
      /* keep current url */
    }
  } else if (pickerProductUrl) {
    try {
      nextUrl = new URL(pickerProductUrl, window.location.origin);
    } catch {
      /* keep current url */
    }
  }

  /** @type {string[]} */
  const optionHandles = [];
  const metaScript = picker.querySelector('[data-piv2-options-meta]');
  if (metaScript?.textContent) {
    try {
      JSON.parse(metaScript.textContent).forEach((axis) => {
        if (axis?.handle) optionHandles.push(String(axis.handle));
      });
    } catch {
      /* ignore malformed meta */
    }
  }

  nextUrl.searchParams.delete('quick_add');
  nextUrl.searchParams.delete('view');
  nextUrl.searchParams.delete('section_id');
  nextUrl.searchParams.delete('variant');
  nextUrl.searchParams.delete('option_values');
  nextUrl.searchParams.delete('color_only');
  nextUrl.searchParams.delete('color');
  nextUrl.searchParams.delete('colour');
  optionHandles.forEach((handle) => nextUrl.searchParams.delete(handle));

  picker.querySelectorAll('.piv2-option-group[data-option-handle]').forEach((group) => {
    if (!(group instanceof HTMLElement)) return;

    const handle = group.dataset.optionHandle?.trim();
    if (!handle) return;

    const selectedBtn = group.querySelector('.piv2-swatch-btn.selected');
    if (!(selectedBtn instanceof HTMLElement)) return;

    const valueName = selectedBtn.dataset.valueName?.trim();
    if (valueName) nextUrl.searchParams.set(handle, valueName);
  });

  const variantInput = scope.querySelector('[data-piv2-variant-id]');
  const variantId = variantInput instanceof HTMLInputElement ? variantInput.value?.trim() : '';
  if (variantId) {
    nextUrl.searchParams.set('variant', variantId);
    ensureVariantFirstQueryParam(nextUrl);
  }

  return nextUrl;
}

/**
 * @param {Element | null | undefined} modalContent
 */
function startPiv2QuickAddAvailability(modalContent) {
  if (!(modalContent instanceof Element)) return;

  const picker = modalContent.querySelector('variant-picker-v2');
  if (picker && typeof picker.startQuickAddAvailability === 'function') {
    picker.startQuickAddAvailability();
  }
}

/**
 * Flip the quick-add media wrapper to `data-media-ready="true"`.
 *
 * The piv2 section server-renders `data-media-ready="false"` and ships no
 * media-ready script of its own (unlike the v1 `product-information-content`
 * snippet, whose inline script never runs here anyway — the markup arrives via
 * DOMParser). Nothing else flips it, so any code that *waits* on the attribute
 * can only ever resolve through its own timeout. Both open paths call this
 * before waiting.
 *
 * @param {Element | null | undefined} modalContent
 */
function markQuickAddMediaReady(modalContent) {
  if (!(modalContent instanceof Element)) return;

  const hasPiv2 = Boolean(modalContent.querySelector('variant-picker-v2'));
  modalContent.querySelectorAll('.product-information__media[data-media-ready]').forEach((wrapper) => {
    if (!(wrapper instanceof HTMLElement)) return;
    if (wrapper.dataset.mediaReady === 'true') return;

    const hasGallery = Boolean(wrapper.querySelector('media-gallery, img, .placeholder-image'));
    if (hasPiv2 || hasGallery) {
      wrapper.dataset.mediaReady = 'true';
    }
  });
}

/**
 * Wait until PIV2 finishes loading encoded availability in quick-add.
 * @param {Element | null | undefined} modalContent
 * @returns {Promise<void>}
 */
async function waitForPiv2AvailabilityReady(modalContent) {
  const picker = modalContent?.querySelector('variant-picker-v2');
  if (!(picker instanceof HTMLElement)) return;

  if (!picker.hasAttribute('data-loading')) return;

  await new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      resolve(undefined);
    };

    const observer = new MutationObserver(() => {
      if (!picker.hasAttribute('data-loading')) finish();
    });
    observer.observe(picker, { attributes: true, attributeFilter: ['data-loading', 'aria-busy'] });
    setTimeout(finish, 15000);
  });
}

/**
 * @param {string} variantId
 * @returns {Promise<string[]|null>}
 */
async function fetchShopifyVariantOptionNames(variantId) {
  if (!variantId) return null;

  const root = typeof window !== 'undefined' && window.Shopify?.routes?.root
    ? window.Shopify.routes.root
    : '/';
  const url = `${root}variants/${encodeURIComponent(String(variantId))}.js`;

  try {
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return null;

    const data = await response.json();
    const optionNames = [data?.option1, data?.option2, data?.option3].filter(Boolean);
    return optionNames.length ? optionNames : null;
  } catch {
    return null;
  }
}

/**
 * @param {Element} modalContent
 * @param {string} variantId
 */
function setPiv2CartVariantInput(modalContent, variantId) {
  if (!variantId) return;

  const input =
    modalContent.querySelector('product-form-component [ref="variantId"]') ||
    modalContent.querySelector('[data-piv2-variant-id]') ||
    modalContent.querySelector('input[name="id"]');

  if (input instanceof HTMLInputElement) {
    input.value = String(variantId);
  }
}

function buildQuickAddViewProductUrl(root, baseHref) {
  const modalContent = getQuickAddContentRoot(root);
  const fallbackUrl =
    (modalContent instanceof HTMLElement ? modalContent.dataset.productUrl : '') ||
    baseHref ||
    '';
  if (!fallbackUrl) return '';

  let url;
  try {
    url = new URL(fallbackUrl, window.location.origin);
  } catch {
    return fallbackUrl;
  }

  const piv2Url = buildPiv2QuickAddViewProductUrl(root, url);
  if (piv2Url) return piv2Url.toString();

  const existingColor = url.searchParams.get('color') || url.searchParams.get('colour') || '';
  let selectedColor = '';
  const setSelectedColor = (value) => {
    const nextValue = typeof value === 'string' ? value.trim() : '';
    if (nextValue) selectedColor = nextValue;
  };

  url.searchParams.delete('color');
  url.searchParams.delete('colour');

  const picker = getActiveQuickAddVariantPicker(root);
  if (picker instanceof HTMLElement) {
    picker.querySelectorAll('fieldset[data-option-name-handle]').forEach((fieldset) => {
      if (!(fieldset instanceof HTMLFieldSetElement)) return;
      const handle = fieldset.dataset.optionNameHandle;
      if (handle !== 'color' && handle !== 'colour') return;

      const checkedInput =
        fieldset.querySelector('input[type="radio"]:checked') ||
        fieldset.querySelector('input[type="radio"][data-current-checked="true"]');
      if (checkedInput instanceof HTMLInputElement) setSelectedColor(checkedInput.value);
    });

    picker.querySelectorAll('select[data-option-name-handle], select.variant-option__select').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      const handle = select.dataset.optionNameHandle;
      if (handle !== 'color' && handle !== 'colour') return;

      setSelectedColor(select.value);
    });
  }

  setSelectedColor(selectedColor || getSelectedQuickAddColorValue(root) || existingColor);
  if (selectedColor) url.searchParams.set('color', selectedColor);

  url.searchParams.delete('quick_add');
  url.searchParams.delete('view');
  url.searchParams.delete('section_id');
  url.searchParams.delete('variant');
  url.searchParams.delete('option_values');
  url.searchParams.delete('color_only');

  return url.toString();
}

function syncQuickAddViewProductButton(button, nextUrl) {
  if (!(button instanceof HTMLButtonElement) || !nextUrl) return;

  button.removeAttribute('onclick');
  button.dataset.productUrl = nextUrl;
  if (button.dataset.quickAddViewProductBound === 'true') return;

  button.dataset.quickAddViewProductBound = 'true';
  button.addEventListener('click', () => {
    window.location.href = button.dataset.productUrl || nextUrl;
  });
}

export class QuickAddComponent extends Component {
  /** @type {Map<string, boolean>} */
  static wishlistLocalStates = new Map();

  /** @type {Set<string>} Cross-context busy lock shared with PDP and cart-drawer. */
  static wishlistBusySet = (() => {
    const w = /** @type {any} */ (window);
    if (!(w.__bonobosWishlistBusy instanceof Set)) w.__bonobosWishlistBusy = new Set();
    return w.__bonobosWishlistBusy;
  })();

  /** @type {Map<string, Promise<void>>} */
  static #piv2SiblingHydrateInflight = new Map();

  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Document | null>>} */
  #inflightFetches = new Map();
  /** @type {string} */
  #activeQuickAddOpenUrl = '';
  /** @type {boolean} */
  #lastQuickAddFetchAborted = false;
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  #variantSelectedHandler = (event) => this.#updateQuickAddButtonState(event);
  #savedItemsChangedHandler = (event) => this.#handleSavedItemsChanged(event);

  // Delegate so instance and shared caches key identically — a prefetch stored
  // under the shared key has to be findable from the click path.
  #getQuickAddUrlKey(productPageUrl) {
    return getQuickAddUrlKey(productPageUrl);
  }

  get isShopTheLookQuickAdd() {
    return (
      this.dataset.quickAddSource === 'shop-the-look' ||
      Boolean(this.closest('[data-stl-card], [data-shop-the-look-inline]'))
    );
  }

  get productCardClickAction() {
    const rawAction = this.dataset.productCardClickAction || '';
    const normalizedAction = rawAction.trim().toLowerCase();
    return normalizedAction === 'quick_add' ? 'quick_add' : 'product_page';
  }

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    let productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    // Fallback: use data-product-url when used outside product-card (e.g. shop-the-look-inline, cart carousel)
    if (!productLink?.href && this.dataset.productUrl) {
      const dataUrl = this.dataset.productUrl.trim();
      if (dataUrl) {
        try {
          const url = new URL(dataUrl, window.location.origin);
          const selectedVariantId = this.#getSelectedVariantId() || this.dataset.variantId || '';
          if (this.productCardClickAction === 'quick_add' && !this.isShopTheLookQuickAdd) {
            const quickAddUrl = new URL(url.pathname, window.location.origin);
            preserveColorParam(url, quickAddUrl);
            this.#syncSelectedOptionHandleParams(quickAddUrl);
            return quickAddUrl.toString();
          }
          if (
            this.productCardClickAction !== 'quick_add' &&
            selectedVariantId &&
            !url.searchParams.has('variant') &&
            !url.searchParams.has('color')
          ) {
            url.searchParams.set('variant', selectedVariantId);
          }
          return url.toString();
        } catch {
          return dataUrl;
        }
      }
    }

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (this.productCardClickAction === 'quick_add' && !this.isShopTheLookQuickAdd) {
      const quickAddUrl = new URL(url.pathname, window.location.origin);
      preserveColorParam(url, quickAddUrl);
      this.#syncSelectedOptionHandleParams(quickAddUrl);
      return quickAddUrl.toString();
    }

    this.#syncSelectedOptionHandleParams(url);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  /**
   * Mirrors the PDP/QV section-rendering contract by carrying explicit option-handle
   * selections from the product tile into the initial quick-view request URL.
   * @param {URL} url
   */
  #syncSelectedOptionHandleParams(url) {
    if (!(url instanceof URL)) return;

    const productCard = this.closest('product-card');
    if (!(productCard instanceof HTMLElement)) return;

    const variantPicker = productCard.querySelector('variant-picker, swatches-variant-picker-component');
    if (!(variantPicker instanceof HTMLElement)) return;

    variantPicker.querySelectorAll('fieldset[data-option-name-handle]').forEach((fieldset) => {
      if (!(fieldset instanceof HTMLFieldSetElement)) return;

      const handle = fieldset.dataset.optionNameHandle;
      if (!handle) return;

      url.searchParams.delete(handle);
      if (this.productCardClickAction === 'quick_add' && handle !== 'color' && handle !== 'colour') return;

      const checkedInput =
        fieldset.querySelector('input[type="radio"]:checked') ||
        fieldset.querySelector('input[type="radio"][data-current-checked="true"]');

      if (checkedInput instanceof HTMLInputElement && checkedInput.value) {
        url.searchParams.set(handle, checkedInput.value);
      }
    });

    variantPicker.querySelectorAll('select[data-option-name-handle], select.variant-option__select').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;

      const handle = select.dataset.optionNameHandle;
      if (!handle) return;

      url.searchParams.delete(handle);
      if (this.productCardClickAction === 'quick_add' && handle !== 'color' && handle !== 'colour') return;

      if (select.value) {
        url.searchParams.set(handle, select.value);
      }
    });
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#variantSelectedHandler);
    document.addEventListener('bonobos:saved-items-changed', this.#savedItemsChangedHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#variantSelectedHandler);
    document.removeEventListener('bonobos:saved-items-changed', this.#savedItemsChangedHandler);
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    const size = this.#cachedContent.size;
    this.#cachedContent.clear();
    this.#inflightFetches.clear();
    clearQuickAddGridCache();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = getActiveQuickAddVariantPicker(modalContent);
    if (!(variantPicker instanceof VariantPicker)) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    if (!currentUrl) {
      console.warn('[QuickAdd] No product URL; skipping (not inside product-card or hotspot?)');
      return;
    }

    if (this.productCardClickAction !== 'quick_add') {
      if (event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1)) {
        window.open(currentUrl, '_blank');
        return;
      }

      window.location.href = currentUrl;
      return;
    }

    const quickAddUrlKey = this.#getQuickAddUrlKey(currentUrl);
    if (this.#activeQuickAddOpenUrl === quickAddUrlKey) {
      console.log('[QuickAdd] Duplicate quick-add open ignored for', currentUrl);
      return;
    }
    this.#activeQuickAddOpenUrl = quickAddUrlKey;
    this.#lastQuickAddFetchAborted = false;

    // Check if we have cached content for this URL — the shared cache catches
    // renders warmed by another card's prefetch or by openQuickAddDialog.
    let productGrid =
      this.#cachedContent.get(quickAddUrlKey) ||
      this.#cachedContent.get(currentUrl) ||
      quickAddGridCache.get(quickAddUrlKey);
    const cacheHit = !!productGrid;

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = extractQuickAddProductGrid(html);
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(quickAddUrlKey, productGrid);
        } else {
          console.warn('[QuickAdd] No [data-product-grid-content] or .product-information__grid in fetched HTML');
        }
      } else {
        console.warn('[QuickAdd] fetchProductPage returned null');
      }
    }

    if (productGrid) {
      // Match React ProductCard: replace modal content with grid clone (no morph, no mobile reorder)
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent) {
        startPiv2QuickAddAvailability(modalContent);
        this.#syncVariantSelection(modalContent);
        this.#clearImplicitQuickAddNonColorSelections(modalContent);
        this.#syncColorSelection(modalContent);
        this.#clearImplicitQuickAddNonColorSelections(modalContent);
        this.#syncShopTheLookQuickAddButtonLabel(modalContent);
        this.#markQuickAddMediaReady(modalContent);
      }
      this.#openQuickAddModal();
      if (modalContent) {
        void this.#finishQuickAddHydration(modalContent);
      }
      requestAnimationFrame(() => {
        const content = document.getElementById('quick-add-modal-content');
        if (content) {
          this.#disableBuyButtonIfUnavailableOptionChecked(content);
          this.#syncShopTheLookQuickAddButtonLabel(content);
        }
      });
      this.#activeQuickAddOpenUrl = '';
    } else {
      this.#activeQuickAddOpenUrl = '';
      if (this.#lastQuickAddFetchAborted) {
        console.warn('[QuickAdd] Quick-add fetch aborted; staying on current page');
        return;
      }
      // No content found (e.g. different product template or fetch failed) — go to product page
      window.location.href = currentUrl;
    }
  };

  /**
   * Native quick-view opens in a partial-selection state: color may be selected,
   * but size/fit/etc. must stay visually unselected until the customer chooses them.
   * @param {Element} modalContent
   */
  #clearImplicitQuickAddNonColorSelections(modalContent) {
    if (!(modalContent instanceof Element) || this.isShopTheLookQuickAdd) return;

    scheduleQuickAddImplicitNonColorSelectionClear(modalContent);
  }

  /** @param {string} productUrl */
  static #handleFromProductUrl(productUrl) {
    try {
      const path = new URL(productUrl, window.location.origin).pathname;
      const match = path.match(/\/products\/([^/?#]+)/);
      return match ? match[1] : '';
    } catch {
      return '';
    }
  }

  /**
   * Data replacement for the old SSR price transplant: seed the header price
   * from the sibling so it never shows the previous product while the
   * selection is partial. The piv2 variant:update dispatched by
   * applySiblingProductContext refines it once a variant resolves — except a
   * persisting range, which product-price deliberately leaves alone.
   * @param {HTMLElement} modalContent
   * @param {any} api
   * @param {any} sibling
   */
  static #applySiblingHeaderPrice(modalContent, api, sibling) {
    const container = modalContent.querySelector(
      '.quick-add-product-details product-price [ref="priceContainer"]'
    );
    if (!(container instanceof HTMLElement) || typeof api.formatMoney !== 'function') return;

    const priced = (sibling.variants || []).filter(
      (variant) => typeof variant?.priceCents === 'number' && !Number.isNaN(variant.priceCents)
    );
    if (!priced.length) return;

    const cents = priced.map((variant) => variant.priceCents);
    const min = Math.min(...cents);
    const max = Math.max(...cents);
    // The variant the header stands in for until a real selection resolves —
    // also the one the compare-at reconcile below is judged against.
    const seedVariant = priced.find((variant) => variant.availableForSale) || priced[0];

    // The volume-pricing range element keeps a marker attribute even while its
    // `price-range` class is stripped, so a later swap BACK to a range-priced
    // sibling restores both the range text and product-price's `.price-range`
    // bail. The header is server-rendered once per modal open and reused for
    // every swap, so a bare classList.remove() would be a one-way door.
    const rangeEl = container.querySelector('.price-range, [data-piv2-price-range]');
    if (rangeEl instanceof HTMLElement) {
      rangeEl.setAttribute('data-piv2-price-range', '');
      if (min !== max) {
        // Same "range persists" behavior the SSR transplant produced.
        rangeEl.textContent = `${api.formatMoney(min)} - ${api.formatMoney(max)}`;
        rangeEl.classList.add('price-range');
      } else {
        // Single-price sibling: defuse product-price's .price-range bail so
        // piv2 pricing updates take over from here.
        rangeEl.textContent = api.formatMoney(min);
        rangeEl.classList.remove('price-range');
      }
      QuickAddComponent.#applySiblingHeaderCompareAt(container, rangeEl, api, seedVariant);
      return;
    }

    const priceEl = container.querySelector('.price:not(.price-range)');
    if (priceEl instanceof HTMLElement) {
      priceEl.textContent = api.formatMoney(seedVariant.priceCents);
    }
    QuickAddComponent.#applySiblingHeaderCompareAt(
      container,
      priceEl instanceof HTMLElement ? priceEl : null,
      api,
      seedVariant
    );
  }

  /**
   * Reconcile the header's compare-at against the SIBLING's own data. Liquid
   * renders `.compare-at-price` inside the same priceContainer
   * (snippets/price.liquid) and nothing else rewrites it while the selection
   * is partial — so without this a swap from a marked-down colour to a
   * full-price one keeps advertising the previous product's markdown.
   *
   * Hidden, never removed, and shown with the same `hidden` + inline-display
   * pair product-price.js uses (`.price-group-wrapper * { display: inline-flex }`
   * otherwise beats the UA `[hidden]` rule), so the two can hand off freely.
   * A sale sibling whose SSR header has no compare-at markup at all is left
   * alone rather than fabricated — product-price injects it for real once a
   * variant resolves.
   * @param {HTMLElement} container
   * @param {HTMLElement | null} priceEl
   * @param {any} api
   * @param {{ priceCents?: number | null, compareAtCents?: number | null }} seedVariant
   */
  static #applySiblingHeaderCompareAt(container, priceEl, api, seedVariant) {
    const compareEl = container.querySelector('.compare-at-price');
    if (!(compareEl instanceof HTMLElement)) return;
    const group = compareEl.closest('[role="group"]');
    const wrapper = group instanceof HTMLElement ? group : compareEl;

    const price = seedVariant?.priceCents;
    const compareAt = seedVariant?.compareAtCents;
    const onSale = typeof price === 'number' && typeof compareAt === 'number' && compareAt > price;

    if (onSale) {
      compareEl.textContent = api.formatMoney(compareAt);
      wrapper.hidden = false;
      wrapper.style.removeProperty('display');
      priceEl?.classList.add('custom-price--highlight');
    } else {
      wrapper.hidden = true;
      wrapper.style.display = 'none';
      priceEl?.classList.remove('custom-price--highlight');
    }
  }

  /**
   * Storefront-data sibling swap for quick view: one Piv2ClSibling load, no
   * view=quick-add HTML fetch — the same source the PDP hydrate uses. Static
   * because the React PLP renders no <quick-add-component> instance.
   *
   * Fail-soft everywhere: Storefront is the sole source (no BFF/HTML
   * fallback, user directive), so any failure unblocks the picker via
   * failSiblingProductContextFetch and the modal stays on the current
   * product. Gallery fidelity refusal is NOT a failure — the old gallery
   * stays (stale images beat an empty gallery; unlike the PDP the modal
   * cannot navigate away).
   * @param {{ productUrl?: string, ownerProductId?: string, ownerHandle?: string }} detail
   */
  static async #runPiv2SiblingDataHydrate(detail) {
    const modalContent = document.getElementById('quick-add-modal-content');
    const picker = modalContent?.querySelector('variant-picker-v2');
    const productUrl = detail?.productUrl?.trim();

    if (!(modalContent instanceof HTMLElement) || !(picker instanceof HTMLElement) || !productUrl) {
      picker?.failSiblingProductContextFetch?.();
      return;
    }

    const api = globalThis.Piv2SiblingProduct;
    const token = (picker.dataset.storefrontToken || '').trim();
    const openUrl = modalContent.dataset.productUrl || '';
    let mediaWrapper = null;

    try {
      if (!api || typeof api.loadSiblingProduct !== 'function' || !token) {
        throw new Error('Storefront sibling module or token unavailable');
      }

      const sibling = await api.loadSiblingProduct({
        token,
        productId: detail.ownerProductId || '',
        handle: detail.ownerHandle || QuickAddComponent.#handleFromProductUrl(productUrl),
        stackNamespace: (picker.dataset.productStackNamespace || '').trim(),
      });

      // The await can outlive the modal (closed, or reopened on another
      // product). Unstick whatever picker is live now and stop.
      const liveContent = document.getElementById('quick-add-modal-content');
      const livePicker = liveContent?.querySelector('variant-picker-v2');
      if (
        liveContent !== modalContent ||
        livePicker !== picker ||
        !picker.isConnected ||
        (modalContent.dataset.productUrl || '') !== openUrl
      ) {
        livePicker?.failSiblingProductContextFetch?.();
        return;
      }

      if (!sibling) throw new Error('sibling product not found');
      // Gift cards need Liquid-only chrome (recipient form) the data path
      // cannot build; effectively unreachable for CL siblings.
      if (sibling.isGiftCard) throw new Error('gift-card sibling not supported in quick view');

      const galleryMedia = api.buildGalleryMedia(sibling);

      mediaWrapper = modalContent.querySelector('.product-information__media');
      if (mediaWrapper instanceof HTMLElement) {
        mediaWrapper.classList.add('quick-add-media--loading');
      }

      // Identity BEFORE the picker apply: the header product-price drops
      // variant:update events whose target carries a foreign product id.
      //
      // The PICKER is deliberately excluded: applySiblingProductData derives
      // the true CL parent from data-product-id (on a role='parent' picker
      // #storefrontParentId reads it), so overwriting it here would make the
      // picker adopt the sibling CHILD as its own parent — poisoning the
      // parent matrix, the colorGroups cache key and every later owner
      // resolve. applySiblingProductContext writes the picker's own id.
      if (sibling.url) modalContent.setAttribute('data-product-url', sibling.url);
      modalContent.querySelectorAll('[data-product-id]').forEach((node) => {
        if (node === picker) return;
        if (sibling.id) node.setAttribute('data-product-id', String(sibling.id));
      });

      QuickAddComponent.#applySiblingHeaderPrice(modalContent, api, sibling);

      if (typeof picker.applySiblingProductData !== 'function') {
        throw new Error('picker lacks applySiblingProductData');
      }
      picker.applySiblingProductData(sibling);

      const gallery = mediaWrapper?.querySelector('media-gallery');
      if (
        galleryMedia.length &&
        gallery &&
        typeof gallery.rebuildFromProductData === 'function' &&
        (typeof gallery.canRebuildFromProductData !== 'function' ||
          gallery.canRebuildFromProductData(galleryMedia))
      ) {
        gallery.rebuildFromProductData({
          media: galleryMedia,
          productUrl: sibling.url || '',
          imageColorGroup: sibling.imageColorGroup || '',
          wishlist: {
            empi: String(sibling.id || ''),
            iu: sibling.featuredImageUrl || '',
            dt: sibling.title || '',
          },
        });
      }

      picker.resetGalleryColorSync?.();
      // Re-filters the rebuilt gallery to the committed color — media-gallery
      // listens on the modal <dialog>.
      picker.dispatchEvent(new CustomEvent('variant-picker:state-sync', { bubbles: true }));

      QuickAddComponent.#syncQuickAddViewProductUrl(
        modalContent,
        modalContent.dataset.productUrl || productUrl
      );

      if (mediaWrapper instanceof HTMLElement) {
        void QuickAddComponent.#finishSiblingMediaLoading(mediaWrapper);
      }
    } catch (err) {
      console.warn('[QuickAdd] PIV2 sibling data hydrate failed', err);
      picker.failSiblingProductContextFetch?.();
      mediaWrapper = modalContent.querySelector('.product-information__media');
      if (mediaWrapper instanceof HTMLElement) {
        mediaWrapper.classList.remove('quick-add-media--loading');
      }
    }
  }

  /**
   * @param {{ productUrl?: string, ownerProductId?: string, ownerHandle?: string, colorValueIndex?: number, colorName?: string }} detail
   */
  static hydratePiv2SiblingInQuickAdd(detail) {
    const productUrl = detail?.productUrl?.trim();
    if (!productUrl) return;

    const inflight = QuickAddComponent.#piv2SiblingHydrateInflight.get(productUrl);
    if (inflight) {
      // A duplicate dispatch normally targets the same picker the in-flight
      // promise will settle against. But after close + reopen the promise
      // holds the OLD detached picker, leaving the new one stuck in
      // #siblingContextPending (now visibly shimmering) — unstick it when
      // the original hydrate settles (no-op when the pickers match: the
      // pending flag was already cleared).
      void inflight.finally(() => {
        const livePicker = document
          .getElementById('quick-add-modal-content')
          ?.querySelector('variant-picker-v2');
        livePicker?.failSiblingProductContextFetch?.();
      });
      return;
    }

    // Static data hydrate — works with or without a <quick-add-component>
    // instance on the page (the React PLP renders none).
    const promise = QuickAddComponent.#runPiv2SiblingDataHydrate(detail);

    QuickAddComponent.#piv2SiblingHydrateInflight.set(productUrl, promise);
    void promise.finally(() => {
      QuickAddComponent.#piv2SiblingHydrateInflight.delete(productUrl);
    });
  }

  /**
   * @param {HTMLElement} mediaWrapper
   */
  static async #finishSiblingMediaLoading(mediaWrapper) {
    const modalContent = mediaWrapper.closest('#quick-add-modal-content, .quick-add-modal__content');
    if (modalContent instanceof HTMLElement) {
      void QuickAddComponent.#primeQuickAddSlideshowImages(modalContent);
    }

    const primaryImage = mediaWrapper.querySelector('.product-media__image, img');
    if (primaryImage instanceof HTMLImageElement && !(primaryImage.complete && primaryImage.naturalWidth > 0)) {
      await Promise.race([
        new Promise((resolve) => {
          primaryImage.addEventListener('load', resolve, { once: true });
          primaryImage.addEventListener('error', resolve, { once: true });
          if (typeof primaryImage.decode === 'function') {
            primaryImage.decode().then(resolve).catch(resolve);
          }
        }),
        new Promise((resolve) => setTimeout(resolve, 180)),
      ]);
    }

    mediaWrapper.classList.remove('quick-add-media--loading');
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    const productCard = this.closest('product-card');

    this.toggleAttribute('stay-visible', true);
    productCard?.setAttribute('quick-add-active', '');

    // dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
    //   once: true,
    // });

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute('stay-visible', false);
        productCard?.removeAttribute('quick-add-active');

        // The dialog close can leave focus on the trigger button, which keeps
        // the SHOP NOW hover/focus styling active after the modal is closed.
        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLElement && this.contains(activeElement)) {
          activeElement.blur();
        }
      },
      {
        once: true,
      }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) {
      console.warn('[QuickAdd] quick-add-dialog not found or not QuickAddDialog');
      return;
    }

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    this.#startModalWishlistObserver();

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => {
      this.#stopModalWishlistObserver();
    }, { once: true });

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Extracts the product grid element from a parsed quick-add page document.
   * @param {Document} html
   * @returns {Element | null}
   */
  #extractProductGrid(html) {
    return extractQuickAddProductGrid(html);
  }

  /**
   * Fetches the product page content. If an identical request is already in
   * flight (e.g. from a hover prefetch), reuses that promise instead of
   * aborting and starting over. Successful fetches also populate
   * `#cachedContent` so subsequent calls (like the click after a hover) hit
   * the cache and skip the network entirely.
   *
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    const quickAddUrlKey = this.#getQuickAddUrlKey(productPageUrl);
    const requestUrl = new URL(productPageUrl, window.location.origin);
    requestUrl.searchParams.delete('breadcrumb_title');
    requestUrl.searchParams.delete('breadcrumb_url');
    requestUrl.searchParams.delete('breadcrumb_child_title');
    requestUrl.searchParams.delete('breadcrumb_child_url');
    requestUrl.searchParams.delete('breadcrumb_current_title');
    requestUrl.searchParams.set('view', 'quick-add');
    requestUrl.searchParams.set('quick_add', '1');
    const urlWithView = requestUrl.toString();

    // Reuse an in-flight fetch for the same URL — important so a click that
    // arrives mid-prefetch doesn't abort and re-issue the request.
    const existing = this.#inflightFetches.get(quickAddUrlKey);
    if (existing) return existing;

    // A hover prefetch may already have this render in the shared grid cache
    // (or in flight there). Adopt it instead of issuing a duplicate request,
    // wrapped in a detached document: callers extract the grid from a
    // Document and own the nodes they move out of it.
    let warmGrid = quickAddGridCache.get(quickAddUrlKey) || null;
    if (!warmGrid) {
      const inflightGrid = quickAddGridInflight.get(quickAddUrlKey);
      if (inflightGrid) {
        await inflightGrid;
        warmGrid = quickAddGridCache.get(quickAddUrlKey) || null;
      }
    }
    if (warmGrid) {
      const doc = document.implementation.createHTMLDocument('');
      doc.body.appendChild(doc.importNode(warmGrid, true));
      return doc;
    }

    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    const generation = quickAddCacheGeneration;
    const fetchPromise = (async () => {
      try {
        const response = await fetch(urlWithView, { signal });

        if (!response.ok) {
          throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
        }

        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Pre-extract and cache the grid so prefetches benefit later clicks.
        // Skip both writes if the cache was cleared mid-flight (cart update) —
        // this render may reflect the pre-update cart state.
        const gridElement = this.#extractProductGrid(html);
        const generationCurrent = generation === quickAddCacheGeneration;
        if (gridElement && generationCurrent && !this.#cachedContent.has(quickAddUrlKey)) {
          this.#cachedContent.set(quickAddUrlKey, /** @type {Element} */ (gridElement.cloneNode(true)));
        }
        // Publish to the shared cache too, so a hover prefetch on a Liquid card
        // also warms openQuickAddDialog.
        if (gridElement && generationCurrent && !quickAddGridCache.has(quickAddUrlKey)) {
          quickAddGridCache.set(quickAddUrlKey, /** @type {Element} */ (gridElement.cloneNode(true)));
        }

        return html;
      } catch (error) {
        if (error.name === 'AbortError') {
          this.#lastQuickAddFetchAborted = true;
          console.log('[QuickAdd] fetch aborted');
          return null;
        } else {
          console.error('[QuickAdd] fetch error:', error);
          throw error;
        }
      } finally {
        this.#inflightFetches.delete(quickAddUrlKey);
        if (this.#abortController?.signal === signal) {
          this.#abortController = null;
        }
      }
    })();

    this.#inflightFetches.set(quickAddUrlKey, fetchPromise);
    return fetchPromise;
  }

  /**
   * Re-renders the quick-add modal content to match the React ProductCard behavior:
   * replaceChildren with the product grid clone (no morph, no mobile-only reorder).
   * @param {Element} productGrid - The product grid element (clone)
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) {
      if (!modalContent) console.warn('[QuickAdd] #quick-add-modal-content not found');
      return;
    }

    // Same as React: replace children with grid clone so layout matches React-triggered drawer
    const clone = productGrid.cloneNode(true);
    transplantQuickAddPowerReviewsPlaceholder(modalContent, clone);
    modalContent.classList.add('quick-add-modal--hydrating');
    if (this.productPageUrl) {
      modalContent.setAttribute('data-product-url', this.productPageUrl);
      if (this.isShopTheLookQuickAdd) {
        modalContent.setAttribute('data-quick-add-source', 'shop-the-look');
      } else {
        modalContent.removeAttribute('data-quick-add-source');
      }
    } else {
      modalContent.removeAttribute('data-quick-add-source');
    }
    if (!this.isShopTheLookQuickAdd) {
      delete modalContent.dataset.quickAddEditLineItemKey;
      delete modalContent.dataset.quickAddLineItemProperties;
      delete modalContent.dataset.quickAddEditSellingPlanId;
      modalContent.classList.remove('quick-add-modal--edit-mode');
    }
    modalContent.replaceChildren(clone);
    if (this.productPageUrl) {
      QuickAddComponent.#syncQuickAddViewProductUrl(modalContent, this.productPageUrl);
    }
    this.#syncQuickAddInitialSubtitle(modalContent);
    this.#bindQuickAddWishlist(modalContent);
    this.#syncShopTheLookQuickAddButtonLabel(modalContent);
  }

  /**
   * Keeps quick-add header links aligned with the exact URL used to open the modal.
   * @param {Element} modalContent
   * @param {string} productPageUrl
   */
  static #syncQuickAddViewProductUrl(modalContent, productPageUrl) {
    if (!(modalContent instanceof Element) || !productPageUrl) return;

    let nextUrl = buildQuickAddViewProductUrl(modalContent, productPageUrl);
    if (!nextUrl) {
      try {
        nextUrl = new URL(productPageUrl, window.location.origin).toString();
      } catch {
        nextUrl = productPageUrl;
      }
    }

    const titleLink = modalContent.querySelector('.quick-add-product-details .quick-add-view-product__title');
    if (titleLink instanceof HTMLAnchorElement) {
      titleLink.removeAttribute('onclick');
      titleLink.href = nextUrl;
      titleLink.dataset.productUrl = nextUrl;
    }

    const viewProductButton = modalContent.querySelector('.quick-add-product-details .quick-add-view-product__button');
    syncQuickAddViewProductButton(viewProductButton, nextUrl);
  }

  /**
   * @param {Element} root
   */
  #bindQuickAddWishlist(root) {
    root.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      if (button.dataset.quickAddWishlistBound === 'true') return;
      button.dataset.quickAddWishlistBound = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.#handleQuickAddWishlistClick(button, root);
      });
      this.#refreshQuickAddWishlistState(button, root);
    });
  }

  /**
   * @param {HTMLElement} button
   * @param {Element} root
   */
  #handleQuickAddWishlistClick(button, root) {
    if (button.hasAttribute('data-wishlist-requires-selection') || button.hasAttribute('data-wishlist-unavailable')) return;
    const payload = this.#buildQuickAddWishlistPayload(button, root);
    if (!payload || button.dataset.wishlistBusy === 'true') return;
    // Drop the click if another context (PDP heart, cart line) is mid-flight
    // for the same variant — prevents a parallel addToWishList that would
    // duplicate the Swym record.
    const lockKey = this.#getWishlistStateKey(payload);
    const wishlistBusy = QuickAddComponent.wishlistBusySet;
    if (lockKey && wishlistBusy.has(lockKey)) return;
    if (lockKey) wishlistBusy.add(lockKey);

    button.dataset.wishlistBusy = 'true';
    const wasWishlisted = button.getAttribute('aria-pressed') === 'true';
    this.#setLocalWishlistState(payload, !wasWishlisted);
    this.#setQuickAddWishlistState(button, !wasWishlisted);
    if (wasWishlisted) {
      this.#forgetPendingSavedItem(payload);
    } else {
      this.#rememberPendingSavedItem(payload);
    }
    this.#dispatchSavedItemsChanged(payload, !wasWishlisted);

    this.#withSwym((swat) => {
      const releaseLock = () => {
        if (lockKey) wishlistBusy.delete(lockKey);
      };
      const onSuccess = () => {
        button.dataset.wishlistBusy = 'false';
        releaseLock();
        if (wasWishlisted) {
          this.#forgetPendingSavedItem(payload);
        } else {
          this.#rememberPendingSavedItem(payload);
        }
        this.#setQuickAddWishlistState(button, !wasWishlisted);
        this.#dispatchSavedItemsChanged(payload, !wasWishlisted);
      };
      const onError = () => {
        button.dataset.wishlistBusy = 'false';
        releaseLock();
        if (wasWishlisted) {
          this.#rememberPendingSavedItem(payload);
        } else {
          this.#forgetPendingSavedItem(payload);
        }
        this.#setLocalWishlistState(payload, wasWishlisted);
        this.#setQuickAddWishlistState(button, wasWishlisted);
      };

      if (wasWishlisted) {
        this.#removeMatchingQuickAddWishlistItems(swat, payload, onSuccess, onError);
      } else {
        swat.addToWishList(payload, onSuccess, onError);
      }
    });
  }

  /**
   * @param {(swat: any) => void} callback
   */
  #withSwym(callback) {
    if (window._swat) {
      callback(window._swat);
      return;
    }
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(callback);
  }

  /**
   * @param {any} swat
   * @param {{ empi: number, epi: number, du?: string }} payload
   * @param {() => void} onSuccess
   * @param {() => void} onError
   */
  #removeMatchingQuickAddWishlistItems(swat, payload, onSuccess, onError) {
    swat.fetch((/** @type {any[]} */ items) => {
      const matches = Array.isArray(items)
        ? items.filter((item) => this.#savedItemMatchesPayload(item, payload))
        : [];

      if (!matches.length) {
        swat.removeFromWishList(payload, onSuccess, onError);
        return;
      }

      let remaining = matches.length;
      let hasError = false;
      const completeOne = () => {
        remaining -= 1;
        if (remaining > 0) return;
        if (hasError) {
          onError();
        } else {
          onSuccess();
        }
      };

      matches.forEach((item) => {
        swat.removeFromWishList(item, completeOne, () => {
          hasError = true;
          completeOne();
        });
      });
    });
  }

  /**
   * @param {HTMLElement} button
   * @param {Element} [root]
   */
  #refreshQuickAddWishlistState(button, root) {
    const payload = this.#buildQuickAddWishlistPayload(button, root || button);
    if (!payload) return;

    this.#withSwym((swat) => {
      swat.fetch((items) => {
        const saved = Array.isArray(items) ? items : [];
        const localState = this.#getLocalWishlistState(payload);
        const isWishlisted = typeof localState === 'boolean'
          ? localState
          : saved.some((item) => this.#savedItemMatchesPayload(item, payload)) || this.#hasPendingSavedItem(payload);
        this.#setQuickAddWishlistState(button, isWishlisted);
      });
    });
  }

  /**
   * @param {HTMLElement} button
   * @param {boolean} isWishlisted
   */
  #setQuickAddWishlistState(button, isWishlisted) {
    const emptyIcon = button.querySelector('[data-wishlist-empty-icon]');
    const filledIcon = button.querySelector('[data-wishlist-filled-icon]');
    button.setAttribute('aria-pressed', String(isWishlisted));
    emptyIcon?.classList.toggle('hidden', isWishlisted);
    filledIcon?.classList.toggle('hidden', !isWishlisted);
  }

  /**
   * @param {{ empi: number, epi: number }} payload
   * @param {boolean} isWishlisted
   */
  #dispatchSavedItemsChanged(payload, isWishlisted) {
    document.dispatchEvent(new CustomEvent('bonobos:saved-items-changed', {
      detail: {
        hasItems: isWishlisted,
        empi: payload.empi,
        epi: payload.epi,
        du: payload.du,
        isWishlisted,
      },
    }));
  }

  #readPendingSavedItems() {
    try {
      const items = JSON.parse(window.localStorage.getItem('bonobos:pending-saved-items') || '[]');
      return Array.isArray(items) ? items : [];
    } catch (_error) {
      return [];
    }
  }

  /**
   * @param {any[]} items
   */
  #writePendingSavedItems(items) {
    try {
      window.localStorage.setItem('bonobos:pending-saved-items', JSON.stringify(items));
    } catch (_error) {
      // Swym remains the source of truth when storage is unavailable.
    }
  }

  /**
   * @param {{ empi: number, epi: number, du?: string }} payload
   */
  #rememberPendingSavedItem(payload) {
    const items = this.#readPendingSavedItems().filter((item) => !this.#savedItemMatchesPayload(item, payload));
    items.unshift({ ...payload, savedAt: Date.now() });
    this.#writePendingSavedItems(items.slice(0, 50));
  }

  /**
   * @param {{ empi: number, epi: number, du?: string }} payload
   */
  #forgetPendingSavedItem(payload) {
    this.#writePendingSavedItems(this.#readPendingSavedItems().filter((item) => !this.#savedItemMatchesPayload(item, payload)));
  }

  /**
   * Color key derived from the product handle so locale prefixes, trailing
   * slashes, or different URL origins still match against Swym records.
   * @param {string} url
   */
  #getProductColorKey(url) {
    if (!url) return '';
    try {
      const parsedUrl = new URL(url, window.location.origin);
      const handle = parsedUrl.pathname.match(/\/products\/([^\/?#]+)/)?.[1]?.toLowerCase() || '';
      const color = parsedUrl.searchParams.get('color')?.trim().toLowerCase();
      return handle && color ? `${handle}?color=${color}` : '';
    } catch (_error) {
      return '';
    }
  }

  /**
   * @param {any} item
   * @param {{ empi: number, epi: number, du?: string }} payload
   */
  #savedItemMatchesPayload(item, payload) {
    if (Number(item?.epi) === payload.epi) return true;

    const itemColorKey = this.#getProductColorKey(item?.du || '');
    const payloadColorKey = this.#getProductColorKey(payload.du || '');
    return Boolean(itemColorKey && payloadColorKey && itemColorKey === payloadColorKey);
  }

  /**
   * @param {{ epi?: number, du?: string }} payload
   */
  #getWishlistStateKey(payload) {
    return this.#getProductColorKey(payload.du || '') || String(payload.epi || '');
  }

  /**
   * @param {{ epi?: number, du?: string }} payload
   */
  #getLocalWishlistState(payload) {
    return QuickAddComponent.wishlistLocalStates.get(this.#getWishlistStateKey(payload));
  }

  /**
   * @param {{ epi?: number, du?: string }} payload
   * @param {boolean} isWishlisted
   */
  #setLocalWishlistState(payload, isWishlisted) {
    const key = this.#getWishlistStateKey(payload);
    if (!key) return;
    QuickAddComponent.wishlistLocalStates.set(key, isWishlisted);
    setTimeout(() => {
      if (QuickAddComponent.wishlistLocalStates.get(key) === isWishlisted) {
        QuickAddComponent.wishlistLocalStates.delete(key);
      }
    }, 2500);
  }

  /**
   * @param {{ empi: number, epi: number, du?: string }} payload
   */
  #hasPendingSavedItem(payload) {
    return this.#readPendingSavedItems().some((item) => this.#savedItemMatchesPayload(item, payload));
  }

  /**
   * @param {HTMLElement} button
   * @param {any} detail
   * @param {Element} root
   */
  #buttonMatchesSavedItemsDetail(button, detail, root) {
    const payload = this.#buildQuickAddWishlistPayload(button, root);
    if (!payload) return false;

    return this.#savedItemMatchesPayload(detail, payload);
  }

  /**
   * @param {Event} event
   */
  #handleSavedItemsChanged(event) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const detail = event instanceof CustomEvent ? event.detail : null;
    const buttons = modalContent.querySelectorAll('[data-wishlist-toggle]');

    buttons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      if (detail && !this.#buttonMatchesSavedItemsDetail(button, detail, modalContent)) return;

      if (typeof detail?.isWishlisted === 'boolean') {
        const payload = this.#buildQuickAddWishlistPayload(button, modalContent);
        if (payload) this.#setLocalWishlistState(payload, detail.isWishlisted);
        this.#setQuickAddWishlistState(button, detail.isWishlisted);
      }
      setTimeout(() => this.#refreshQuickAddWishlistState(button, modalContent), 500);
    });
  }

  /**
   * @param {HTMLElement} button
   * @param {Element} root
   */
  #buildQuickAddWishlistPayload(button, root) {
    const empi = Number.parseInt(button.dataset.wishlistEmpi || '', 10);
    const epi = this.#getQuickAddWishlistVariantId(button, root);
    if (!empi || !epi) return null;

    const color = this.#getQuickAddSelectedColor(root);
    const productUrl = this.#buildQuickAddWishlistUrl(
      root instanceof HTMLElement && root.dataset.productUrl ? root.dataset.productUrl : button.dataset.wishlistDu || '',
      color
    );

    return {
      empi,
      epi,
      du: productUrl,
      iu: button.dataset.wishlistIu || '',
      dt: button.dataset.wishlistDt || this.dataset.productTitle || '',
      pr: Number.parseFloat(button.dataset.wishlistPr || '0') || 0,
    };
  }

  /**
   * @param {HTMLElement} button
   * @param {Element} root
   */
  #getQuickAddWishlistVariantId(button, root) {
    const formVariantInput = root.querySelector('product-form-component [ref="variantId"], product-form-component input[name="id"]');
    if (formVariantInput instanceof HTMLInputElement && formVariantInput.value) {
      return Number.parseInt(formVariantInput.value, 10);
    }

    const checkedVariantInput = root.querySelector('input[type="radio"][data-variant-id]:checked');
    if (checkedVariantInput instanceof HTMLInputElement && checkedVariantInput.dataset.variantId) {
      return Number.parseInt(checkedVariantInput.dataset.variantId, 10);
    }

    return Number.parseInt(button.dataset.wishlistEpi || '', 10);
  }

  /**
   * @param {Element} root
   */
  #getQuickAddSelectedColor(root) {
    const picker = getActiveQuickAddVariantPicker(root) || root.querySelector('variant-picker');
    if (!(picker instanceof Element)) return '';

    const colorFieldset = picker.querySelector('fieldset[data-option-name-handle="color"]');
    const checkedColor = colorFieldset?.querySelector('input[type="radio"]:checked');
    if (checkedColor instanceof HTMLInputElement && checkedColor.value) return checkedColor.value;

    const colorSelect = picker.querySelector('select.variant-option__select[data-option-name-handle="color"]');
    if (colorSelect instanceof HTMLSelectElement && colorSelect.value) return colorSelect.value;

    return '';
  }

  /**
   * @param {string} baseUrl
   * @param {string} color
   */
  #buildQuickAddWishlistUrl(baseUrl, color) {
    if (!baseUrl) return '';
    try {
      const url = new URL(baseUrl, window.location.origin);
      Array.from(url.searchParams.keys()).forEach((key) => url.searchParams.delete(key));
      if (color) url.searchParams.set('color', color);
      return url.toString();
    } catch (_error) {
      return baseUrl;
    }
  }

  #modalWishlistObserver = null;

  #startModalWishlistObserver() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    this.#modalWishlistObserver?.disconnect();

    this.#modalWishlistObserver = new MutationObserver(() => {
      const btn = modalContent.querySelector('[data-wishlist-toggle]');
      if (!btn) return;
      if (btn.dataset.quickAddWishlistBound === 'true') return;
      this.#bindQuickAddWishlist(modalContent);
    });

    this.#modalWishlistObserver.observe(modalContent, { childList: true, subtree: true });
  }

  #stopModalWishlistObserver() {
    this.#modalWishlistObserver?.disconnect();
    this.#modalWishlistObserver = null;
  }

  #syncQuickAddInitialSubtitle(modalContent) {
    if (!(modalContent instanceof HTMLElement)) return;

    const subtitleElement = modalContent.querySelector('.quick-add-view-product__subtitle');
    if (!(subtitleElement instanceof HTMLElement)) return;

    const hasUnavailableChecked =
      modalContent.querySelector('input[data-option-available="false"]:checked') ||
      modalContent.querySelector('input[data-option-available="false"][data-current-checked="true"]');

    if (!hasUnavailableChecked) {
      subtitleElement.classList.remove('variant-option__error--out-of-stock');
      return;
    }

    subtitleElement.textContent = 'out of stock';
    subtitleElement.classList.add('variant-option__error--out-of-stock');
  }

  /**
   * Give the primary quick-add image a brief chance to decode before opening.
   * @param {HTMLElement} modalContent
   * @returns {Promise<void>}
   */
  async #waitForQuickAddPrimaryImage(modalContent) {
    const primaryImage = modalContent.querySelector(
      '.product-information__media .product-media__image, .product-information__media img'
    );
    if (!(primaryImage instanceof HTMLImageElement)) return;
    if (primaryImage.complete && primaryImage.naturalWidth > 0) return;

    await Promise.race([
      new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          resolve(null);
        };

        primaryImage.addEventListener('load', finish, { once: true });
        primaryImage.addEventListener('error', finish, { once: true });

        if (typeof primaryImage.decode === 'function') {
          primaryImage.decode().then(finish).catch(finish);
        }
      }),
      new Promise((resolve) => setTimeout(resolve, 180)),
    ]);
  }

  /**
   * Warm slideshow images in Quick View so dot navigation doesn't land on an undecoded slide.
   * @param {HTMLElement} modalContent
   */
  static async #primeQuickAddSlideshowImages(modalContent) {
    const slideshowImages = Array.from(
      modalContent.querySelectorAll('.product-information__media slideshow-slide img')
    ).filter((image) => image instanceof HTMLImageElement);

    if (!slideshowImages.length) return;

    slideshowImages.forEach((image, index) => {
      if (!(image instanceof HTMLImageElement)) return;

      image.loading = 'eager';
      image.fetchPriority = index === 0 ? 'high' : 'low';
    });

    const warmupTasks = slideshowImages.map((image, index) => {
      if (!(image instanceof HTMLImageElement)) return Promise.resolve();
      if (image.complete && image.naturalWidth > 0) return Promise.resolve();

      image.fetchPriority = index < 4 ? 'high' : 'low';

      return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          resolve(null);
        };

        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });

        if (typeof image.decode === 'function') {
          image.decode().then(finish).catch(finish);
        }
      });
    });

    await Promise.race([
      Promise.allSettled(warmupTasks),
      new Promise((resolve) => setTimeout(resolve, 600)),
    ]);

    slideshowImages.forEach((image, index) => {
      if (!(image instanceof HTMLImageElement)) return;

      if (image.complete && image.naturalWidth > 0) return;

      if (typeof image.decode === 'function') {
        image.decode().catch(() => {});
      }
    });
  }

  /**
   * PIV2 quick view has no section media-ready script; mark renderable once mounted.
   * @param {Element} modalContent
   */
  #markQuickAddMediaReady(modalContent) {
    markQuickAddMediaReady(modalContent);
  }

  /**
   * Warm images and clear hydrating state after the modal is visible.
   * @param {HTMLElement} modalContent
   */
  async #finishQuickAddHydration(modalContent) {
    await QuickAddComponent.#primeQuickAddSlideshowImages(modalContent);
    await this.#waitForQuickAddPrimaryImage(modalContent);

    if (!modalContent.querySelector('variant-picker-v2')) {
      await this.#waitForQuickAddMediaReady(modalContent);
    }

    modalContent.classList.remove('quick-add-modal--hydrating');
  }

  /**
   * Wait until quick-add media is marked renderable, or time out.
   * @param {HTMLElement} modalContent
   * @returns {Promise<void>}
   */
  #waitForQuickAddMediaReady(modalContent) {
    const mediaWrapper = modalContent.querySelector('.product-information__media[data-media-ready]');
    if (!(mediaWrapper instanceof HTMLElement)) return Promise.resolve();
    if (mediaWrapper.dataset.mediaReady === 'true') return Promise.resolve();

    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        resolve();
      };

      const observer = new MutationObserver(() => {
        if (mediaWrapper.dataset.mediaReady === 'true') {
          finish();
        }
      });

      observer.observe(mediaWrapper, { attributes: true, attributeFilter: ['data-media-ready'] });
      setTimeout(finish, 1200);
    });
  }

  /**
   * Se o conteúdo tiver opção indisponível selecionada (data-option-available="false" + checked ou data-current-checked="true"),
   * desativa todos os botões de add-to-cart no modal. O QV pode ter mais de um botão (ex.: form principal + sticky).
   * @param {Element} modalContent - #quick-add-modal-content
   */
  #disableBuyButtonIfUnavailableOptionChecked(modalContent) {
    const hasUnavailableChecked =
      modalContent.querySelector('input[data-option-available="false"]:checked') ||
      modalContent.querySelector('input[data-option-available="false"][data-current-checked="true"]');
    if (!hasUnavailableChecked) return;

    const addToCartContainers = modalContent.querySelectorAll('add-to-cart-component');
    addToCartContainers.forEach((container) => {
      if (/** @type {HTMLElement & { disable?: () => void }} */ (container).disable) {
        /** @type {HTMLElement & { disable?: () => void }} */ (container).disable();
      }
    });

    const atcButtons = modalContent.querySelectorAll(
      'product-form-component [ref="addToCartButton"], .add-to-cart-button'
    );
    atcButtons.forEach((el) => {
      if (el instanceof HTMLButtonElement) {
        el.disabled = true;
        el.dataset.variantAvailable = 'false';
      }
    });
  }

  /**
   * STL opens QV with color-only partial selection, but its CTA copy should match normal QV.
   * @param {Element} modalContent
   */
  static syncShopTheLookQuickAddButtonLabel(modalContent) {
    if (!(modalContent instanceof Element)) return;

    // "Show this offer's discounted price in the Add to Bag button" (per offer):
    // when the modal's buy button is stamped (data-promo-cta-price on the root,
    // snippets/buy-buttons-v2.liquid / blocks/buy-buttons.liquid) and the price
    // block's visible "$X with code" line belongs to an offer with that setting
    // on (data-promo-cta-price on the LINE, snippets/price-discount-code-line.liquid
    // / buildPromoLineHtml), the button copies that amount — the same resolver
    // output the picker prices the CTA from — instead of the regular price. Any
    // other line, a hidden-price offer or no offer falls through to the regular
    // price as before.
    const promoCtaRoot = modalContent.querySelector(
      '[data-buy-buttons-v2][data-promo-cta-price="true"], product-form-component[data-promo-cta-price="true"]'
    );
    const promoAmountText = promoCtaRoot
      ? modalContent
          .querySelector(
            '.quick-add-product-details product-price .price__discount-code-line:not([hidden])[data-promo-cta-price="true"]:not([data-promo-hide-price="true"]) .price__discount-code-amount'
          )
          ?.textContent?.trim()
      : '';
    const currentPriceText =
      promoAmountText ||
      modalContent
        .querySelector('.quick-add-product-details product-price .custom-price--highlight, .quick-add-product-details product-price .price')
        ?.textContent?.trim();

    modalContent.querySelectorAll('.add-to-cart-text__content-text').forEach((textElement) => {
      if (!(textElement instanceof HTMLElement)) return;
      const currentLabel = textElement.textContent?.trim().toLowerCase();
      if (currentLabel === 'select options') {
        textElement.textContent = 'ADD TO BAG';
      }

      if (!currentPriceText) return;

      const textWrapper = textElement.parentElement;
      if (!(textWrapper instanceof HTMLElement)) return;

      let priceElement = textWrapper.querySelector('.add-to-cart-text__price');
      if (!(priceElement instanceof HTMLElement)) {
        priceElement = document.createElement('span');
        priceElement.className = 'add-to-cart-text__price';
        priceElement.style.marginLeft = '4px';
        textWrapper.appendChild(priceElement);
      }

      priceElement.textContent = currentPriceText;
    });
  }

  #syncShopTheLookQuickAddButtonLabel(modalContent) {
    QuickAddComponent.syncShopTheLookQuickAddButtonLabel(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    if (!this.isShopTheLookQuickAdd) return;
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;
    QuickAddComponent.syncVariantSelectionById(modalContent, selectedVariantId);
  }

  /**
   * Applies a color-only quick-add URL, preserving partial selection behavior.
   * @param {Element} modalContent - The modal content element
   */
  #syncColorSelection(modalContent) {
    if (!(modalContent instanceof Element)) return;

    let url;
    try {
      url = new URL(this.productPageUrl || '', window.location.origin);
    } catch {
      return;
    }

    if (url.searchParams.has('variant') || url.searchParams.has('option_values')) return;
    const requestedColor = url.searchParams.get('color') || url.searchParams.get('colour') || '';
    if (!requestedColor.trim()) return;

    QuickAddComponent.syncColorSelectionByValue(modalContent, requestedColor);
  }

  /**
   * Syncs only color in the modal. Non-color options remain unselected.
   * @param {Element} modalContent - The modal content element
   * @param {string} requestedColor - Color option value from the URL
   */
  static syncColorSelectionByValue(modalContent, requestedColor) {
    if (!(modalContent instanceof Element) || !requestedColor) return;

    if (QuickAddComponent.syncPiv2ColorSelectionByValue(modalContent, requestedColor)) return;

    const normalizeOptionValue = normalizeQuickAddOptionValue;

    const requestedColorHandle = normalizeOptionValue(requestedColor);
    const activeVariantPicker = getActiveQuickAddVariantPicker(modalContent);
    let changeTarget = null;

    modalContent.querySelectorAll('fieldset[data-option-name-handle]').forEach((fieldset) => {
      if (!(fieldset instanceof HTMLFieldSetElement)) return;
      const handle = fieldset.dataset.optionNameHandle || '';
      const isColorFieldset = handle === 'color' || handle === 'colour';

      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        const inputValueHandle = normalizeOptionValue(input.value || '');
        const inputLabelHandle = normalizeOptionValue(input.getAttribute('aria-label') || '');
        const isMatch = isColorFieldset && (inputValueHandle === requestedColorHandle || inputLabelHandle === requestedColorHandle);

        input.checked = isMatch;
        input.dataset.currentChecked = isMatch ? 'true' : 'false';
        if (isMatch) changeTarget = input;
      });
    });

    modalContent.querySelectorAll('select[data-option-name-handle], select.variant-option__select[data-option-name-handle]').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      const handle = select.dataset.optionNameHandle || '';
      const isColorSelect = handle === 'color' || handle === 'colour';

      if (!isColorSelect) {
        select.selectedIndex = -1;
        return;
      }

      const matchingOption = Array.from(select.options).find((option) => {
        const optionValueHandle = normalizeOptionValue(option.value || '');
        const optionLabelHandle = normalizeOptionValue(option.label || option.textContent || '');
        return optionValueHandle === requestedColorHandle || optionLabelHandle === requestedColorHandle;
      });

      if (matchingOption) {
        select.value = matchingOption.value;
        changeTarget = select;
      }
    });

    if (activeVariantPicker instanceof VariantPicker) {
      activeVariantPicker.syncSelectionStateUI();
      QuickAddComponent.syncQuickAddColorOutOfStockLegend(activeVariantPicker);
      requestAnimationFrame(() => activeVariantPicker.syncSelectionStateUI());
      requestAnimationFrame(() => QuickAddComponent.syncQuickAddColorOutOfStockLegend(activeVariantPicker));
      setTimeout(() => QuickAddComponent.syncQuickAddColorOutOfStockLegend(activeVariantPicker), 150);
    }

    if (changeTarget instanceof HTMLElement) {
      changeTarget.dispatchEvent(new Event('change', { bubbles: true }));
      requestAnimationFrame(() => QuickAddComponent.syncQuickAddColorOutOfStockLegend(modalContent));
      setTimeout(() => QuickAddComponent.syncQuickAddColorOutOfStockLegend(modalContent), 150);
    }
  }

  /**
   * Keeps QV color labels aligned with PDP when a selected color is unavailable.
   * @param {Element} root
   */
  static syncQuickAddColorOutOfStockLegend(root) {
    if (!(root instanceof Element) || !root.closest('quick-add-dialog')) return;

    const colorFieldsets = Array.from(
      root.querySelectorAll('fieldset[data-option-name-handle="color"], fieldset[data-option-name-handle="colour"]')
    ).filter((fieldset) => fieldset instanceof HTMLFieldSetElement);
    if (!colorFieldsets.length) return;

    colorFieldsets.forEach((colorFieldset) => {
      const swatchValue = colorFieldset.querySelector('.variant-option__swatch-value');
      if (!(swatchValue instanceof HTMLElement)) return;

      const selectedColorInput =
        colorFieldset.querySelector('input[type="radio"]:checked') ||
        colorFieldset.querySelector('input[type="radio"][data-current-checked="true"]');
      if (!(selectedColorInput instanceof HTMLInputElement)) return;

      const selectedColorLabel = selectedColorInput.closest('label.variant-option__button-label');
      const selectedColorSwatch = selectedColorLabel?.querySelector('.swatch');
      const hasUnavailableSwatch =
        selectedColorSwatch instanceof HTMLElement &&
        selectedColorInput.dataset.optionAvailable === 'false' &&
        (selectedColorSwatch.classList.contains('swatch--unavailable') ||
          selectedColorSwatch.classList.contains('swatch--unavailable--strike-through'));
      const relatedProductAvailability = selectedColorInput.dataset.relatedProductAvailable;
      const isColorInputUnavailable =
        typeof relatedProductAvailability === 'string'
          ? relatedProductAvailability === 'false'
          : selectedColorInput.dataset.optionAvailable === 'false';
      const isSelectedColorUnavailable =
        isColorInputUnavailable ||
        hasUnavailableSwatch;

      if (isSelectedColorUnavailable) {
        swatchValue.textContent = 'out of stock';
        swatchValue.classList.remove('variant-option__selected-value', 'variant-option__error');
        swatchValue.classList.add('variant-option__error--out-of-stock');
        swatchValue.classList.remove('is-visible');
        return;
      }

      if (swatchValue.classList.contains('variant-option__error--out-of-stock')) {
        swatchValue.textContent =
          selectedColorInput.dataset.optionDisplayValue?.trim() ||
          selectedColorInput.getAttribute('aria-label')?.trim() ||
          selectedColorInput.value ||
          '';
        swatchValue.classList.remove('variant-option__error--out-of-stock');
      }

      if (selectedColorSwatch instanceof HTMLElement) {
        selectedColorSwatch.classList.remove('swatch--unavailable', 'swatch--unavailable--strike-through');
      }
      selectedColorLabel?.querySelector('.variant-option__swatch-tooltip--out-of-stock-wrapper')?.remove();
    });
  }

  /**
   * @param {Element} root
   * @returns {boolean}
   */
  static areAllQuickAddNonColorOptionsUnavailable(root) {
    if (!(root instanceof Element)) return false;

    const nonColorFieldsets = Array.from(root.querySelectorAll('fieldset[data-option-name-handle]')).filter((fieldset) => {
      if (!(fieldset instanceof HTMLFieldSetElement)) return false;
      const handle = fieldset.dataset.optionNameHandle || '';
      return handle !== 'color' && handle !== 'colour';
    });

    return nonColorFieldsets.some((fieldset) => {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]')).filter(
        (radio) => radio instanceof HTMLInputElement
      );
      return radios.length > 0 && radios.every((radio) => radio.dataset.optionAvailable === 'false' || radio.disabled);
    });
  }

  /**
   * Syncs the variant selection in the modal to a specific variant ID (e.g. from cart edit).
   * @param {Element} modalContent - The modal content element
   * @param {string} variantId - The variant ID to select
   */
  static async syncVariantSelectionById(modalContent, variantId) {
    if (!variantId) return;
    if (await QuickAddComponent.syncPiv2VariantSelectionById(modalContent, variantId)) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    const modalSelects = modalContent.querySelectorAll('select option[data-variant-id]');
    const activeVariantPicker = getActiveQuickAddVariantPicker(modalContent);
    /** @type {HTMLInputElement[]} */
    const matchingInputs = [];
    /** @type {HTMLSelectElement[]} */
    const changedSelects = [];

    for (const input of modalInputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      const isMatch = input.dataset.variantId === variantId;
      input.dataset.currentChecked = isMatch ? 'true' : 'false';
      if (isMatch) {
        input.checked = true;
        matchingInputs.push(input);
      }
    }

    for (const option of modalSelects) {
      if (!(option instanceof HTMLOptionElement)) continue;
      const isMatch = option.dataset.variantId === variantId;
      option.selected = isMatch;
      if (isMatch && option.parentElement instanceof HTMLSelectElement) {
        changedSelects.push(option.parentElement);
      }
    }

    const syncPickerLabels = () => {
      if (activeVariantPicker instanceof VariantPicker) {
        activeVariantPicker.syncSelectionStateUI();
      }
    };
    syncPickerLabels();
    requestAnimationFrame(syncPickerLabels);
    const finalChangeTarget =
      matchingInputs.at(-1) ||
      changedSelects.at(-1) ||
      Array.from(modalContent.querySelectorAll('select')).find((select) => select instanceof HTMLSelectElement);

    if (finalChangeTarget instanceof HTMLElement) {
      finalChangeTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * @param {Element} modalContent
   * @param {string} requestedColor
   * @returns {boolean}
   */
  static syncPiv2ColorSelectionByValue(modalContent, requestedColor) {
    const picker = modalContent.querySelector('variant-picker-v2');
    if (!(picker instanceof HTMLElement)) return false;

    const metaScript = picker.querySelector('[data-piv2-options-meta]');
    let colorOptionIndex = -1;

    if (metaScript?.textContent) {
      try {
        const meta = JSON.parse(metaScript.textContent);
        colorOptionIndex = meta.findIndex(
          (axis) => axis?.handle === 'color' || axis?.handle === 'colour'
        );
      } catch {
        colorOptionIndex = -1;
      }
    }

    if (colorOptionIndex < 0) return false;

    const requestedColorHandle = normalizeQuickAddOptionValue(requestedColor);
    const buttons = picker.querySelectorAll(`.piv2-swatch-btn[data-option-index="${colorOptionIndex}"]`);
    let match = null;

    buttons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      const valueHandle = normalizeQuickAddOptionValue(button.dataset.valueHandle || button.dataset.valueName || '');
      if (valueHandle === requestedColorHandle) {
        match = button;
      }
    });

    if (!(match instanceof HTMLElement)) return false;
    match.click();
    return true;
  }

  /**
   * @param {Element} modalContent
   * @param {string} variantId
   * @returns {Promise<boolean>}
   */
  static async syncPiv2VariantSelectionById(modalContent, variantId) {
    const picker = modalContent.querySelector('variant-picker-v2');
    if (!(picker instanceof HTMLElement)) return false;

    const metaScript = picker.querySelector('[data-piv2-options-meta]');
    if (!metaScript?.textContent) return false;

    /** @type {Array<{ handle?: string }>} */
    let optionMeta = [];
    try {
      optionMeta = JSON.parse(metaScript.textContent);
    } catch {
      return false;
    }

    /** @type {string[]|null} */
    let optionNames = null;

    const variantsScript = picker.querySelector('[data-piv2-variants]');
    if (variantsScript?.textContent) {
      try {
        const variants = JSON.parse(variantsScript.textContent);
        const embedded = variants.find((entry) => String(entry?.id) === String(variantId));
        if (embedded?.options?.length) {
          optionNames = embedded.options;
        }
      } catch {
        /* ignore */
      }
    }

    if (!optionNames?.length) {
      optionNames = await fetchShopifyVariantOptionNames(variantId);
    }

    if (!optionNames?.length) return false;

    /** @type {Record<string, string>} */
    const valuesByHandle = {};
    optionMeta.forEach((axis, optionIndex) => {
      const handle = axis?.handle;
      const valueName = optionNames[optionIndex];
      if (handle && valueName) valuesByHandle[handle] = valueName;
    });

    if (typeof picker.applySelectionByOptionNames === 'function') {
      picker.applySelectionByOptionNames(valuesByHandle);
      setPiv2CartVariantInput(modalContent, variantId);
      return true;
    }

    optionNames.forEach((optionValue, optionIndex) => {
      const targetHandle = normalizeQuickAddOptionValue(optionValue);
      const buttons = picker.querySelectorAll(`.piv2-swatch-btn[data-option-index="${optionIndex}"]`);
      let match = null;

      buttons.forEach((button) => {
        if (!(button instanceof HTMLElement)) return;
        const valueHandle = normalizeQuickAddOptionValue(
          button.dataset.valueHandle || button.dataset.valueName || ''
        );
        if (valueHandle === targetHandle) {
          match = button;
        }
      });

      match?.click();
    });

    setPiv2CartVariantInput(modalContent, variantId);
    return true;
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  constructor() {
    super();
    const baseCloseDialog = this.closeDialog;
    this.closeDialog = async () => {
      const cartDrawer = /** @type {HTMLElement & { refs?: { dialog?: HTMLDialogElement } }} */ (
        document.querySelector('cart-drawer-component')
      );
      const cartDialog = cartDrawer?.refs?.dialog;
      const isCartDrawerOpen = cartDialog instanceof HTMLDialogElement && cartDialog.open;
      this.skipScrollRestoreOnClose = !!isCartDrawerOpen;
      await baseCloseDialog();
      this.skipScrollRestoreOnClose = false;
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener('variant-picker:state-sync', this.#syncProductDetailLinksFromPickerState);
    this.addEventListener('piv2:sibling-hydrate', this.#handlePiv2SiblingHydrate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener('piv2:quick-add-sibling-hydrate', this.#handlePiv2SiblingHydrate, {
      signal: this.#abortController.signal,
    });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) {
      console.log('[QuickAdd] Cart update had error — not closing dialog');
      return;
    }
    this.closeDialog();
  };

  #handlePiv2SiblingHydrate = (event) => {
    if (!(event instanceof CustomEvent)) return;
    const detail = event.detail || {};
    const context = detail.context;
    if (context === 'pdp' || context === 'bundle') return;

    const modalContent = this.querySelector('#quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) return;
    if (event.target instanceof Node && !modalContent.contains(event.target)) return;

    void QuickAddComponent.hydratePiv2SiblingInQuickAdd(detail);
  };

  #syncProductDetailLinksFromPickerState = (event) => {
    const modalContent = this.querySelector('#quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) return;
    if (event.target instanceof Node && !modalContent.contains(event.target)) return;

    const titleLink = /** @type {HTMLAnchorElement | null} */ (
      modalContent.querySelector('.quick-add-product-details .quick-add-view-product__title')
    );
    const viewProductButton = /** @type {HTMLButtonElement | null} */ (
      modalContent.querySelector('.quick-add-product-details .quick-add-view-product__button')
    );
    const mobileProductTitle = /** @type {HTMLAnchorElement | null} */ (this.querySelector('.product-header a'));
    const baseHref = modalContent.dataset.productUrl || titleLink?.dataset.productUrl || titleLink?.href || '';
    const nextUrl = this.#buildQuickAddViewProductUrl(baseHref);
    if (!nextUrl) return;

    if (titleLink) {
      titleLink.removeAttribute('onclick');
      titleLink.href = nextUrl;
      titleLink.dataset.productUrl = nextUrl;
    }
    if (mobileProductTitle) mobileProductTitle.href = nextUrl;
    syncQuickAddViewProductButton(viewProductButton, nextUrl);
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const dataHtml = event.detail.data.html;
    if (!dataHtml) return;

    const newQuickAddDetails = dataHtml.querySelector('.quick-add-product-details');
    const anchorElement = /** @type {HTMLAnchorElement | null} */ (
      newQuickAddDetails?.querySelector('.quick-add-view-product__title') || dataHtml.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement | null} */ (
      this.querySelector('.quick-add-product-details .quick-add-view-product__title') || this.querySelector('.view-product-title a')
    );
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));
    const currentQuickAddButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.quick-add-product-details .quick-add-view-product__button')
    );
    const newQuickAddButton = /** @type {HTMLButtonElement | null} */ (
      newQuickAddDetails?.querySelector('.quick-add-view-product__button') || null
    );

    const selectedProductUrl = this.#buildQuickAddViewProductUrl(anchorElement?.href || '');

    if (anchorElement && viewMoreDetailsLink) {
      viewMoreDetailsLink.removeAttribute('onclick');
      viewMoreDetailsLink.href = selectedProductUrl || anchorElement.href;
      viewMoreDetailsLink.textContent = anchorElement.textContent;
    }
    if (anchorElement && mobileProductTitle) mobileProductTitle.href = selectedProductUrl || anchorElement.href;
    if (anchorElement && mobileProductTitle && !mobileProductTitle.textContent?.trim()) {
      mobileProductTitle.textContent = anchorElement.textContent;
    }
    if (currentQuickAddButton) {
      const nextButtonUrl = selectedProductUrl || anchorElement?.href || '';
      if (nextButtonUrl) {
        syncQuickAddViewProductButton(currentQuickAddButton, nextButtonUrl);
      } else if (newQuickAddButton) {
        currentQuickAddButton.setAttribute('onclick', newQuickAddButton.getAttribute('onclick') || '');
      }
    }

    this.#syncQuickAddHeaderPrice(dataHtml, event.detail?.resource);
    this.#syncQuickAddSubtitle(dataHtml, event.detail?.resource);
    QuickAddComponent.syncQuickAddColorOutOfStockLegend(this);
    QuickAddComponent.syncShopTheLookQuickAddButtonLabel(this);
    requestAnimationFrame(() => QuickAddComponent.syncQuickAddColorOutOfStockLegend(this));
    requestAnimationFrame(() => QuickAddComponent.syncShopTheLookQuickAddButtonLabel(this));
  };

  #buildQuickAddViewProductUrl(baseHref) {
    return buildQuickAddViewProductUrl(this, baseHref);
  }

  #syncQuickAddHeaderPrice(dataHtml, variantResource = null) {
    const headerPriceContainer = this.querySelector('.quick-add-product-details product-price [ref="priceContainer"]');
    if (!(headerPriceContainer instanceof HTMLElement)) return;

    const nextHeaderPriceContainer = dataHtml?.querySelector('.quick-add-product-details product-price [ref="priceContainer"]');
    if (nextHeaderPriceContainer instanceof HTMLElement) {
      headerPriceContainer.innerHTML = nextHeaderPriceContainer.innerHTML;
      return;
    }

    if (!variantResource || typeof variantResource.price !== 'number') return;

    const priceText = this.#formatQuickAddMoney(variantResource.price);
    const compareAtPriceText =
      typeof variantResource.compare_at_price === 'number' && variantResource.compare_at_price > variantResource.price
        ? this.#formatQuickAddMoney(variantResource.compare_at_price)
        : '';

    if (compareAtPriceText) {
      headerPriceContainer.innerHTML = `
        <span class="price-group-wrapper">
          <span role="group">
            <span class="visually-hidden">Regular price&nbsp;</span>
            <span class="compare-at-price">${compareAtPriceText}</span>
          </span>
          <span role="group">
            <span class="visually-hidden">Sale price&nbsp;</span>
            <span class="price custom-price--highlight">${priceText}</span>
          </span>
        </span>
      `;
      return;
    }

    headerPriceContainer.innerHTML = `<span class="price-group-wrapper"><span class="price">${priceText}</span></span>`;
  }

  #formatQuickAddMoney(amountInCents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: amountInCents % 100 === 0 ? 0 : 2,
    }).format(amountInCents / 100);
  }

  #syncQuickAddSubtitle(dataHtml, variantResource = null) {
    const currentQuickAddDetails = this.querySelector('.quick-add-product-details');
    if (!(currentQuickAddDetails instanceof HTMLElement)) return;

    const isResolvedVariantOutOfStock = Boolean(variantResource && variantResource.available === false);
    const serverSubtitle = dataHtml.querySelector('.quick-add-view-product__subtitle')?.textContent?.trim() || '';
    const selectedColorSubtitle =
      this.#getQuickAddSubtitleFromVariantResource(variantResource) || this.#getSelectedQuickAddColorText();
    const nextSubtitle = isResolvedVariantOutOfStock ? 'out of stock' : selectedColorSubtitle || serverSubtitle;

    let subtitleElement = currentQuickAddDetails.querySelector('.quick-add-view-product__subtitle');

    if (!nextSubtitle) {
      subtitleElement?.remove();
      return;
    }

    if (!(subtitleElement instanceof HTMLElement)) {
      subtitleElement = document.createElement('p');
      subtitleElement.className = 'quick-add-view-product__subtitle';
      const titleElement = currentQuickAddDetails.querySelector('.quick-add-view-product__title');
      if (titleElement) {
        titleElement.insertAdjacentElement('afterend', subtitleElement);
      } else {
        currentQuickAddDetails.prepend(subtitleElement);
      }
    }

    subtitleElement.textContent = nextSubtitle;
    subtitleElement.classList.toggle('variant-option__error--out-of-stock', isResolvedVariantOutOfStock);
  }

  #getSelectedQuickAddColorText() {
    const colorFieldset = this.querySelector(
      'variant-picker fieldset[data-option-name-handle="color"], variant-picker fieldset[data-option-name-handle="colour"]'
    );
    if (!(colorFieldset instanceof HTMLFieldSetElement)) return '';

    const checkedInput =
      colorFieldset.querySelector('input[type="radio"]:checked') ||
      colorFieldset.querySelector('input[type="radio"][data-current-checked="true"]');

    if (!(checkedInput instanceof HTMLInputElement)) return '';

    return checkedInput.value?.trim() || checkedInput.getAttribute('aria-label')?.trim() || '';
  }

  #getQuickAddSubtitleFromVariantResource(variantResource) {
    if (!variantResource || typeof variantResource !== 'object') return '';

    const colorFieldset = this.querySelector(
      'variant-picker fieldset[data-option-name-handle="color"], variant-picker fieldset[data-option-name-handle="colour"]'
    );
    if (colorFieldset instanceof HTMLFieldSetElement) {
      const firstInput = colorFieldset.querySelector('input[data-input-id]');
      const optionPosition = Number.parseInt(firstInput?.dataset.inputId?.split('-')[0] || '', 10);
      const optionValue = variantResource[`option${optionPosition}`];
      if (typeof optionValue === 'string' && optionValue.trim()) {
        return optionValue.trim();
      }
    }

    const colorSelect = this.querySelector(
      'variant-picker select[data-option-name-handle="color"], variant-picker select[data-option-name-handle="colour"]'
    );
    if (colorSelect instanceof HTMLSelectElement) {
      const firstOption = colorSelect.querySelector('option[data-input-id]');
      const optionPosition = Number.parseInt(firstOption?.dataset.inputId?.split('-')[0] || '', 10);
      const optionValue = variantResource[`option${optionPosition}`];
      if (typeof optionValue === 'string' && optionValue.trim()) {
        return optionValue.trim();
      }
    }

    return '';
  }

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

/**
 * Cart drawer Edit button: open quick-add modal in "edit" mode.
 * Passes line item key so on "Update" we remove the line then add the selected variant.
 * Uses window capture for submit so our handler runs before the product form's document-level handler
 * (otherwise the form adds the new variant before we remove the old line).
 *
 * Cart line item must have: data-quick-add-edit, data-product-url, data-line-item-key, data-variant-id.
 * Optional: data-line-item-quantity (falls back to cart row quantity input).
 */
(function () {
  const CART_CHANGE_JS_URL =
    (typeof Theme !== 'undefined' && Theme.routes?.cart_change_url
      ? Theme.routes.cart_change_url.replace(/\/change\/?$/, '') + '/change.js'
      : '/cart/change.js');

  const getCartAddUrl = () =>
    (typeof Theme !== 'undefined' && Theme.routes?.cart_add_url) ? Theme.routes.cart_add_url : '/cart/add.js';

  /**
   * @param {HTMLElement} editBtn
   * @returns {number|null}
   */
  function getCartEditLineItemQuantity(editBtn) {
    const fromDataset = parseInt(editBtn.dataset.lineItemQuantity || '', 10);
    if (!Number.isNaN(fromDataset) && fromDataset > 0) return fromDataset;

    const cartRow = editBtn.closest('.cart-product');
    const cartQtyInput = cartRow?.querySelector('input[data-cart-quantity]');
    if (cartQtyInput instanceof HTMLInputElement) {
      const fromInput = parseInt(cartQtyInput.value, 10);
      if (!Number.isNaN(fromInput) && fromInput > 0) return fromInput;
    }

    return null;
  }

  /**
   * @param {HTMLElement} modalContent
   * @param {number} quantity
   */
  function applyQuickAddEditLineQuantity(modalContent, quantity) {
    if (!modalContent || !Number.isFinite(quantity) || quantity <= 0) return;

    modalContent.dataset.quickAddEditLineItemQuantity = String(quantity);

    const input = modalContent.querySelector('input[name="quantity"]');
    if (!(input instanceof HTMLInputElement)) return;

    input.value = String(quantity);

    const quantitySelector = input.closest('quantity-selector-component');
    if (
      quantitySelector instanceof HTMLElement &&
      typeof /** @type {{ updateButtonStates?: () => void }} */ (quantitySelector).updateButtonStates === 'function'
    ) {
      /** @type {{ updateButtonStates: () => void }} */ (quantitySelector).updateButtonStates();
    }

    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  const pendingStorageKey = 'bonobos:pending-saved-items';

  /** @param {(swat: any) => void} callback */
  function withSwym(callback) {
    if (window._swat) {
      callback(window._swat);
      return;
    }
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(callback);
  }

  function readPendingSavedItems() {
    try {
      const items = JSON.parse(window.localStorage.getItem(pendingStorageKey) || '[]');
      return Array.isArray(items) ? items : [];
    } catch (_error) {
      return [];
    }
  }

  /** @param {any[]} items */
  function writePendingSavedItems(items) {
    try {
      window.localStorage.setItem(pendingStorageKey, JSON.stringify(items));
    } catch (_error) {
      // Swym remains the source of truth when storage is unavailable.
    }
  }

  /** @param {string} url */
  function productColorKey(url) {
    if (!url) return '';
    try {
      const parsedUrl = new URL(url, window.location.origin);
      const handle = parsedUrl.pathname.match(/\/products\/([^\/?#]+)/)?.[1]?.toLowerCase() || '';
      const color = parsedUrl.searchParams.get('color')?.trim().toLowerCase();
      return handle && color ? `${handle}?color=${color}` : '';
    } catch (_error) {
      return '';
    }
  }

  /** @param {HTMLElement} button */
  function setWishlistState(button, isWishlisted) {
    const emptyIcon = button.querySelector('[data-wishlist-empty-icon]');
    const filledIcon = button.querySelector('[data-wishlist-filled-icon]');
    button.setAttribute('aria-pressed', String(isWishlisted));
    emptyIcon?.classList.toggle('hidden', isWishlisted);
    filledIcon?.classList.toggle('hidden', !isWishlisted);
  }

  /** @param {Element} root */
  function getEditSelectedColor(root) {
    const picker = getActiveQuickAddVariantPicker(root) || root.querySelector('variant-picker');
    if (!(picker instanceof Element)) return '';

    const colorFieldset = picker.querySelector('fieldset[data-option-name-handle="color"]');
    const checkedColor = colorFieldset?.querySelector('input[type="radio"]:checked');
    if (checkedColor instanceof HTMLInputElement && checkedColor.value) return checkedColor.value;

    const colorSelect = picker.querySelector('select.variant-option__select[data-option-name-handle="color"]');
    if (colorSelect instanceof HTMLSelectElement && colorSelect.value) return colorSelect.value;

    return '';
  }

  /**
   * @param {string} baseUrl
   * @param {string} color
   */
  function buildEditWishlistUrl(baseUrl, color) {
    if (!baseUrl) return '';
    try {
      const url = new URL(baseUrl, window.location.origin);
      Array.from(url.searchParams.keys()).forEach((key) => url.searchParams.delete(key));
      if (color) url.searchParams.set('color', color);
      return url.toString();
    } catch (_error) {
      return baseUrl;
    }
  }

  /**
   * @param {HTMLElement} button
   * @param {Element} root
   */
  function buildEditWishlistPayload(button, root) {
    const empi = Number.parseInt(button.dataset.wishlistEmpi || '', 10);
    const variantInput = root.querySelector('product-form-component [ref="variantId"], product-form-component input[name="id"]');
    const epi = variantInput instanceof HTMLInputElement && variantInput.value
      ? Number.parseInt(variantInput.value, 10)
      : Number.parseInt(button.dataset.wishlistEpi || '', 10);
    if (!empi || !epi) return null;

    return {
      empi,
      epi,
      du: buildEditWishlistUrl(
        root instanceof HTMLElement && root.dataset.productUrl ? root.dataset.productUrl : button.dataset.wishlistDu || '',
        getEditSelectedColor(root)
      ),
      iu: button.dataset.wishlistIu || '',
      dt: button.dataset.wishlistDt || '',
      pr: Number.parseFloat(button.dataset.wishlistPr || '0') || 0,
    };
  }

  /**
   * @param {any} item
   * @param {{ empi: number, epi: number, du?: string }} payload
   */
  function savedItemMatchesPayload(item, payload) {
    if (Number(item?.epi) === payload.epi) return true;
    const itemColorKey = productColorKey(item?.du || '');
    const payloadColorKey = productColorKey(payload.du || '');
    return Boolean(itemColorKey && payloadColorKey && itemColorKey === payloadColorKey);
  }

  /**
   * @param {any} item
   * @param {{ empi: number, epi: number, du?: string }} payload
   */
  function savedItemMatchesEditState(item, payload) {
    return savedItemMatchesPayload(item, payload);
  }

  /** @param {{ empi: number, epi: number, du?: string }} payload */
  function rememberPendingSavedItem(payload) {
    const items = readPendingSavedItems().filter((item) => !savedItemMatchesPayload(item, payload));
    items.unshift({ ...payload, savedAt: Date.now() });
    writePendingSavedItems(items.slice(0, 50));
  }

  /** @param {{ empi: number, epi: number, du?: string }} payload */
  function forgetPendingSavedItem(payload) {
    writePendingSavedItems(readPendingSavedItems().filter((item) => !savedItemMatchesPayload(item, payload)));
  }

  /** @param {{ empi: number, epi: number, du?: string }} payload */
  function hasPendingSavedItem(payload) {
    return readPendingSavedItems().some((item) => savedItemMatchesPayload(item, payload));
  }

  /**
   * @param {{ empi: number, epi: number, du?: string }} payload
   * @param {boolean} isWishlisted
   */
  function dispatchSavedItemsChanged(payload, isWishlisted) {
    document.dispatchEvent(new CustomEvent('bonobos:saved-items-changed', {
      detail: {
        hasItems: isWishlisted,
        empi: payload.empi,
        epi: payload.epi,
        du: payload.du,
        isWishlisted,
      },
    }));
  }

  /**
   * @param {any} swat
   * @param {{ empi: number, epi: number, du?: string }} payload
   * @param {() => void} onSuccess
   * @param {() => void} onError
   */
  function removeMatchingWishlistItems(swat, payload, onSuccess, onError) {
    swat.fetch((items) => {
      const matches = Array.isArray(items)
        ? items.filter((item) => savedItemMatchesPayload(item, payload))
        : [];

      if (!matches.length) {
        swat.removeFromWishList(payload, onSuccess, onError);
        return;
      }

      let remaining = matches.length;
      let hasError = false;
      const completeOne = () => {
        remaining -= 1;
        if (remaining > 0) return;
        if (hasError) {
          onError();
        } else {
          onSuccess();
        }
      };

      matches.forEach((item) => {
        swat.removeFromWishList(item, completeOne, () => {
          hasError = true;
          completeOne();
        });
      });
    });
  }

  /** @param {Element} root */
  function bindEditWishlist(root) {
    if (root instanceof HTMLElement && root.dataset.editWishlistDelegated !== 'true') {
      root.dataset.editWishlistDelegated = 'true';
      root.addEventListener('click', (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-wishlist-toggle]') : null;
        if (!(button instanceof HTMLElement)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
        handleEditWishlistClick(button, root);
      }, true);
    }

    root.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      button.dataset.wishlistBusy = 'false';
      const payload = buildEditWishlistPayload(button, root);
      if (!payload) return;
      withSwym((swat) => {
        swat.fetch((items) => {
          const saved = Array.isArray(items) ? items : [];
          setWishlistState(button, saved.some((item) => savedItemMatchesEditState(item, payload)) || hasPendingSavedItem(payload));
        });
      });
    });
  }

  /**
   * @param {HTMLElement} button
   * @param {Element} root
   */
  function handleEditWishlistClick(button, root) {
    const payload = buildEditWishlistPayload(button, root);
    if (!payload) return;

    button.dataset.wishlistBusy = 'true';
    const wasWishlisted = button.getAttribute('aria-pressed') === 'true';
    const nextState = !wasWishlisted;
    setWishlistState(button, nextState);
    if (nextState) {
      rememberPendingSavedItem(payload);
    } else {
      forgetPendingSavedItem(payload);
    }
    dispatchSavedItemsChanged(payload, nextState);

    withSwym((swat) => {
      const onSuccess = () => {
        button.dataset.wishlistBusy = 'false';
        setWishlistState(button, nextState);
        if (nextState) {
          rememberPendingSavedItem(payload);
        } else {
          forgetPendingSavedItem(payload);
        }
        dispatchSavedItemsChanged(payload, nextState);
      };
      const onError = () => {
        button.dataset.wishlistBusy = 'false';
        setWishlistState(button, wasWishlisted);
        if (wasWishlisted) {
          rememberPendingSavedItem(payload);
        } else {
          forgetPendingSavedItem(payload);
        }
      };

      if (nextState) {
        swat.addToWishList(payload, onSuccess, onError);
      } else {
        removeMatchingWishlistItems(swat, payload, onSuccess, onError);
      }
    });
  }

  function scheduleOpenEditWishlistRefresh() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent?.classList.contains('quick-add-modal--edit-mode')) return;

    bindEditWishlist(modalContent);
    requestAnimationFrame(() => bindEditWishlist(modalContent));
    setTimeout(() => bindEditWishlist(modalContent), 150);
    setTimeout(() => bindEditWishlist(modalContent), 500);
  }

  ['variant:update', 'variant:selected', 'variant-picker:state-sync'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      const modalContent = document.getElementById('quick-add-modal-content');
      if (!modalContent?.classList.contains('quick-add-modal--edit-mode')) return;
      if (event.target instanceof Node && !modalContent.contains(event.target)) return;
      scheduleOpenEditWishlistRefresh();
    }, true);
  });

  document.addEventListener('change', (event) => {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent?.classList.contains('quick-add-modal--edit-mode')) return;
    if (!(event.target instanceof Element) || !modalContent.contains(event.target)) return;
    if (!event.target.closest('variant-picker')) return;
    scheduleOpenEditWishlistRefresh();
  }, true);

  /** @type {((ev: Event) => void) | null} */
  let documentSubmitCapture = null;
  const hydratedGiftCardRecipientForms = new WeakSet();

  // Cart-edit re-hydration on variant change is intentionally NOT wired up
  // here anymore. The gift-card recipient form persists its own state via
  // sessionStorage (keyed by line item key in cart-edit mode) and restores
  // itself on every variant:update, so re-applying the cart's original
  // line-item properties after each variant change would clobber the user's
  // in-progress edits. The initial hydration on modal open still runs via
  // the direct hydrateGiftCardRecipientForm(...) call in the edit-button
  // click handler below.
  //
  // Previously this file listened for both `quick-add:edit-grid-replaced`
  // (dispatched by variant-picker after its morph) and ThemeEvents.variantUpdate
  // (debounced by 50ms) — both calling hydrateGiftCardRecipientForm with
  // force=true. That was the root cause of "edits reset to initial position
  // when changing value in cart-edit Quick View."

  /**
   * Wait until quick-add media is marked renderable, or time out.
   * @param {HTMLElement} modalContent
   * @returns {Promise<void>}
   */
  function waitForQuickAddMediaReady(modalContent) {
    const mediaWrapper = modalContent.querySelector('.product-information__media[data-media-ready]');
    if (!(mediaWrapper instanceof HTMLElement)) return Promise.resolve();
    if (mediaWrapper.dataset.mediaReady === 'true') return Promise.resolve();

    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        resolve();
      };

      const observer = new MutationObserver(() => {
        if (mediaWrapper.dataset.mediaReady === 'true') {
          finish();
        }
      });

      observer.observe(mediaWrapper, { attributes: true, attributeFilter: ['data-media-ready'] });
      setTimeout(finish, 1200);
    });
  }

  /**
   * Give the primary quick-add image a brief chance to decode before opening.
   * @param {HTMLElement} modalContent
   * @returns {Promise<void>}
   */
  async function waitForQuickAddPrimaryImage(modalContent) {
    const primaryImage = modalContent.querySelector(
      '.product-information__media .product-media__image, .product-information__media img'
    );
    if (!(primaryImage instanceof HTMLImageElement)) return;
    if (primaryImage.complete && primaryImage.naturalWidth > 0) return;

    await Promise.race([
      new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          resolve(null);
        };

        primaryImage.addEventListener('load', finish, { once: true });
        primaryImage.addEventListener('error', finish, { once: true });

        if (typeof primaryImage.decode === 'function') {
          primaryImage.decode().then(finish).catch(finish);
        }
      }),
      new Promise((resolve) => setTimeout(resolve, 180)),
    ]);
  }

  /**
   * @param {FormData} formData
   * @returns {Record<string, FormDataEntryValue>}
   */
  function getLineItemProperties(formData) {
    /** @type {Record<string, FormDataEntryValue>} */
    const properties = {};

    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (!match || !match[1] || value === '') return;
      properties[match[1]] = value;
    });

    return properties;
  }

  /**
   * @param {string | undefined} rawProperties
   * @returns {Record<string, string>}
   */
  function parseLineItemProperties(rawProperties) {
    if (!rawProperties) return {};

    try {
      const parsed = JSON.parse(rawProperties);
      /** @type {Record<string, string>} */
      const properties = {};

      if (Array.isArray(parsed)) {
        parsed.forEach((entry) => {
          if (Array.isArray(entry) && entry.length >= 2) {
            properties[String(entry[0])] = String(entry[1] ?? '');
          } else if (entry && typeof entry === 'object') {
            const key = entry.first ?? entry.name ?? entry.key;
            const value = entry.last ?? entry.value;
            if (key) properties[String(key)] = String(value ?? '');
          }
        });
      } else if (parsed && typeof parsed === 'object') {
        Object.entries(parsed).forEach(([key, value]) => {
          properties[key] = String(value ?? '');
        });
      }

      return properties;
    } catch {
      return {};
    }
  }

  /**
   * @param {string} value
   */
  function formatGiftCardDateForDisplay(value) {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return value;

    return `${match[2]}/${match[3]}/${match[1]}`;
  }

  /**
   * Case/whitespace-tolerant lookup for Shopify line item property labels.
   * @param {Record<string, string>} properties
   * @param {string} canonicalKey
   * @returns {string}
   */
  function getLineItemPropertyCI(properties, canonicalKey) {
    const nk = canonicalKey.replace(/\s+/g, ' ').trim().toLowerCase();
    const direct = properties[canonicalKey];
    if (direct) return direct;
    const entry = Object.entries(properties).find(([k]) => k.replace(/\s+/g, ' ').trim().toLowerCase() === nk);
    return entry?.[1] ? String(entry[1]) : '';
  }

  /**
   * @param {HTMLElement} modalContent
   * @param {string | undefined} rawProperties
   * @param {{ force?: boolean }} [options]
   */
  async function hydrateGiftCardRecipientForm(modalContent, rawProperties, options = {}) {
    const { force = false } = options;
    const properties = parseLineItemProperties(rawProperties);
    const recipientEmail = getLineItemPropertyCI(properties, 'Recipient email');
    if (!recipientEmail) return;

    await customElements.whenDefined('gift-card-recipient-form');
    if (typeof customElements.upgrade === 'function') {
      customElements.upgrade(modalContent);
    }

    const scopedForms = Array.from(modalContent.querySelectorAll('product-form-component gift-card-recipient-form'));
    const recipientForms =
      scopedForms.length > 0
        ? scopedForms
        : Array.from(modalContent.querySelectorAll('gift-card-recipient-form'));

    if (recipientForms.length === 0) return;

    const recipientName = getLineItemPropertyCI(properties, 'Recipient name');
    const message = getLineItemPropertyCI(properties, 'Message');
    const sendOn = getLineItemPropertyCI(properties, 'Send on');

    const gcCtor = customElements.get('gift-card-recipient-form');
    const payload = { recipientEmail, recipientName, message, sendOn };

    for (const recipientForm of recipientForms) {
      if (!(recipientForm instanceof HTMLElement)) continue;
      if (!force && hydratedGiftCardRecipientForms.has(recipientForm)) {
        continue;
      }

      const recipientFormGiftCard =
        gcCtor && recipientForm instanceof gcCtor
          ? /** @type {HTMLElement & { applyQuickAddCartEditRecipientData?: (data: object) => void }} */ (
              recipientForm
            )
          : null;
      if (
        recipientFormGiftCard &&
        typeof recipientFormGiftCard.applyQuickAddCartEditRecipientData === 'function'
      ) {
        recipientFormGiftCard.applyQuickAddCartEditRecipientData(payload);
        hydratedGiftCardRecipientForms.add(recipientForm);
        continue;
      }

      const formElement = /** @type {HTMLElement & { toggleRecipientForm?: (mode: string) => void }} */ (recipientForm);
      if (typeof formElement.toggleRecipientForm === 'function') {
        formElement.toggleRecipientForm('recipient_form');
      }

      const fieldValues = {
        'properties[Recipient email]': recipientEmail,
        'properties[Recipient name]': recipientName || '',
        'properties[Message]': message || '',
        'properties[Send on]': sendOn || '',
      };

      Object.entries(fieldValues).forEach(([name, value]) => {
        const candidates = Array.from(recipientForm.querySelectorAll('input, textarea')).filter((input) => {
          if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) return false;
          return input.name === name || input.dataset.originalName === name;
        });
        const field = candidates.find(
          (input) => !(input instanceof HTMLInputElement && input.type === 'hidden')
        ) ?? candidates[0];
        if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;

        field.value = name === 'properties[Send on]' && field.type === 'text' ? formatGiftCardDateForDisplay(value) : value;
        field.dispatchEvent(new Event('input', { bubbles: true }));
      });
      hydratedGiftCardRecipientForms.add(recipientForm);
    }
  }

  /**
   * Capture submit on window so we run before product form's document listener.
   * When in edit mode: remove old line (change qty to 0) then add new variant.
   * @param {Event} ev
   */
  function handleDocumentSubmitCapture(ev) {
    if (ev.type !== 'submit') return;
    const form = ev.target instanceof HTMLFormElement ? ev.target : null;
    if (!form) return;
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent || !modalContent.dataset.quickAddEditLineItemKey) return;
    if (!modalContent.contains(form)) return;

    ev.preventDefault();
    ev.stopImmediatePropagation();

    const key = modalContent.dataset.quickAddEditLineItemKey;
    const picker = modalContent.querySelector('variant-picker-v2');
    if (picker?.isCartSubmitting?.()) return;

    const dialogComponent = document.getElementById('quick-add-dialog');
    const addToCartError = modalContent.querySelector('[ref="addToCartTextError"]');

    (async () => {
      try {
        if (typeof picker?.setCartSubmitting === 'function') {
          picker.setCartSubmitting(true);
        }

        const formData = new FormData(form);
        // The modal hides its quantity selector, so the user can't change the
        // quantity here — and async piv2 re-renders can reset the form input
        // back to 1. The line quantity captured when edit mode opened wins.
        const storedQuantity = parseInt(modalContent.dataset.quickAddEditLineItemQuantity || '', 10);
        const formQuantity = parseInt(String(formData.get('quantity')), 10);
        const quantity = [storedQuantity, formQuantity].find((q) => Number.isFinite(q) && q > 0) || 1;
        const properties = getLineItemProperties(formData);
        const stackRole = form.closest('[data-product-stack-role]')?.dataset?.productStackRole;
        if (stackRole === 'base' || stackRole === 'plain') {
          properties._product_stack_role = stackRole;
        }
        const sellingPlanIdRaw = modalContent.dataset.quickAddEditSellingPlanId;
        const sellingPlanId = sellingPlanIdRaw ? Number(sellingPlanIdRaw) : NaN;
        const sellingPlanPayload = Number.isFinite(sellingPlanId) ? { selling_plan: sellingPlanId } : {};

        // Base→stack swapping happens in assets/product-stack-hijack.js, which
        // rewrites the /cart/add.js payload below — no per-surface resolve here.
        // The picker still owns the *selection*, though, so ask it every time
        // rather than trusting the form's hidden id: that input is seeded from
        // Liquid and #updateVariantInput only ever overwrites it with a non-null
        // id, so it is never empty and can still be holding the previously
        // selected variant while a remote resolve (truncated product,
        // combined-listing sibling) is in flight or has just returned nothing.
        // ensureRemoteVariantResolved awaits that in-flight resolve and returns
        // the local variant with no network call when there is one.
        let variantIdVal = formData.get('id');
        if (picker && typeof picker.ensureRemoteVariantResolved === 'function') {
          try {
            const resolved = await picker.ensureRemoteVariantResolved();
            if (resolved?.id != null && resolved.id !== '') variantIdVal = resolved.id;
          } catch (resolveErr) {
            console.error('[QuickAdd] Remote variant resolve failed:', resolveErr);
          }
        }

        if (!variantIdVal) {
          showInlineCartError(addToCartError, 'Please select a variant');
          return;
        }

        const changeRes = await fetch(CART_CHANGE_JS_URL, fetchConfig('json', {
          body: JSON.stringify({ id: key, quantity: 0 }),
        }));
        const changeData = await changeRes.json();
        if (changeData.message) {
          showInlineCartError(addToCartError, changeData.message);
          return;
        }

        // Request cart section renders with the add so listeners (cart page,
        // cart drawer) can morph in place instead of re-fetching.
        const cartSectionIds = getCartItemsSectionIds();
        const addRes = await fetch(getCartAddUrl(), fetchConfig('json', {
          body: JSON.stringify({
            items: [
              {
                id: variantIdVal,
                quantity,
                ...(Object.keys(properties).length ? { properties } : {}),
                ...sellingPlanPayload,
              },
            ],
            ...(cartSectionIds.length
              ? {
                  sections: cartSectionIds.join(','),
                  sections_url: cartSectionsUrl(),
                }
              : {}),
          }),
        }));
        const addData = await addRes.json();
        if (!addRes.ok || addData.status || addData.description) {
          // Old line is already gone — restore it before handleCartAddFailure
          // so that function's /cart.js sync + cart:update reflect the restored cart.
          let restoreFailed = false;
          try {
            const originalVariantId = parseInt(String(key).split(':')[0], 10);
            const originalProperties = parseLineItemProperties(
              modalContent.dataset.quickAddLineItemProperties
            );
            if (Number.isFinite(originalVariantId)) {
              const restoreRes = await fetch(getCartAddUrl(), fetchConfig('json', {
                body: JSON.stringify({
                  id: originalVariantId,
                  quantity,
                  ...(Object.keys(originalProperties).length ? { properties: originalProperties } : {}),
                  ...sellingPlanPayload,
                }),
              }));
              const restoreData = await restoreRes.json().catch(() => ({}));
              if (!restoreRes.ok || restoreData.status || restoreData.description) {
                restoreFailed = true;
              }
            } else {
              restoreFailed = true;
            }
          } catch {
            restoreFailed = true;
          }

          await handleCartAddFailure({
            errorData: addData,
            response: addRes,
            errorEl: addToCartError,
            sourceId: 'quick-add-edit',
            variantId: variantIdVal,
            productId: picker?.dataset?.productId,
          });
          if (restoreFailed) {
            const existing =
              addToCartError?.querySelector?.('.product-form-text__error-message')?.textContent?.trim() ||
              '';
            const restoreMsg = 'Your original item could not be restored to the bag.';
            showInlineCartError(
              addToCartError,
              existing ? `${existing} ${restoreMsg}` : restoreMsg
            );
          }
          return;
        }

        delete modalContent.dataset.quickAddEditLineItemKey;
        delete modalContent.dataset.quickAddLineItemProperties;
        delete modalContent.dataset.quickAddEditSellingPlanId;

        // change.js always returns item_count; add.js never silently clamps
        // (over-requests 422 into the failure branch), so this sum is exact.
        const itemCount =
          typeof changeData.item_count === 'number' ? changeData.item_count + quantity : null;

        document.dispatchEvent(
          new CartUpdateEvent(addData, 'quick-add-edit', {
            source: 'quick-add-edit',
            ...(itemCount != null ? { itemCount } : {}),
            sections: addData.sections,
          })
        );
        if (dialogComponent instanceof QuickAddDialog) dialogComponent.closeDialog();
      } catch (err) {
        console.error('[QuickAdd] Edit update error:', err);
        showInlineCartError(
          addToCartError,
          err instanceof Error ? err.message : 'Something went wrong'
        );
      } finally {
        if (typeof picker?.setCartSubmitting === 'function') {
          picker.setCartSubmitting(false);
        }
      }
    })();
  }

  /**
   * Elevar dl_view_item for the quick view. Fetches the product JSON (full data
   * incl. sku / vendor / product_type) and pushes once per open, for the variant
   * shown (the passed variantId, else the first available). Prices are cents.
   * @param {string} productUrl
   * @param {string} [variantId]
   */
  async function trackQuickAddViewItem(productUrl, variantId, selectedColor) {
    try {
      if (!window.BonobosElevar) return;
      const path = new URL(productUrl, window.location.origin).pathname;
      // A colour swatch opens the modal with a colour but no variant id — carry
      // the colour (from the arg or the product URL) so we track the variant the
      // shopper actually sees, not the first-available one.
      const urlColor = (() => {
        try {
          const u = new URL(productUrl, window.location.origin);
          return u.searchParams.get('color') || u.searchParams.get('colour') || '';
        } catch (_e) {
          return '';
        }
      })();
      const color = selectedColor || urlColor || '';
      const res = await fetch(`${path}.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const product = await res.json();
      const variants = Array.isArray(product.variants) ? product.variants : [];
      // The colour from the URL / swatch is often a handle (e.g. "pink-dune")
      // while variant option values are display names ("Pink Dune"), so compare
      // both normalized.
      const normColor = (s) =>
        String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      const colorKey = normColor(color);
      const matchesColor = (v) =>
        !!colorKey && Array.isArray(v.options) && v.options.some((o) => normColor(o) === colorKey);
      const variant =
        (variantId && variants.find((v) => String(v.id) === String(variantId))) ||
        (color && variants.find((v) => v.available && matchesColor(v))) ||
        (color && variants.find((v) => matchesColor(v))) ||
        variants.find((v) => v.available) ||
        variants[0];
      if (!variant) return;
      const image =
        (variant.featured_image && variant.featured_image.src) ||
        product.featured_image ||
        (product.images && product.images[0]) ||
        '';
      window.BonobosElevar.pushViewItem(
        {
          // Real SKU is available in product JSON; fall back to variant_id.
          id: variant.sku || String(variant.id),
          product_id: String(product.id),
          variant_id: String(variant.id),
          name: product.title,
          brand: product.vendor,
          category: product.type,
          variant: variant.public_title || variant.title,
          price: (variant.price != null ? variant.price : 0) / 100,
          compare_at_price: variant.compare_at_price != null ? variant.compare_at_price / 100 : undefined,
          image: image,
          url: `${window.location.origin}${product.url || path}`,
        },
        window.location.pathname,
      );
    } catch (_e) {
      /* Tracking must never break the quick-add. */
    }
  }

  async function openQuickAddDialog(productUrl, opts = {}) {
    const { variantId, selectedColor } = opts;

    const dialogComponent = document.getElementById('quick-add-dialog');
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!dialogComponent || !modalContent) {
      window.location.href = productUrl;
      return false;
    }

    // Once the drawer is visible, a later failure must not yank the shopper to
    // the PDP — they'd see the modal flash open then vanish. Redirect only for
    // failures that happen before the open.
    let dialogOpened = false;
    try {
      const url = new URL(productUrl, window.location.origin);
      // Prefer the color param the PLP card already encoded in productUrl — the
      // normalized handle (e.g. "pink-dune-plaid") the PDP resolves correctly.
      // `selectedColor` is a raw swatch display name (e.g. "Pink Dune Plaid");
      // sent to the `view=quick-add` fetch it fails server-side color
      // resolution and the modal falls back to the default swatch. It stays as
      // a fallback only for when productUrl carries no color param.
      const requestedColor = url.searchParams.get('color') || url.searchParams.get('colour') || selectedColor || '';
      if (!variantId) {
        url.search = '';
        if (requestedColor) url.searchParams.set('color', requestedColor);
      }
      const stateUrl = new URL(url.pathname, window.location.origin);
      if (variantId) {
        stateUrl.searchParams.set('variant', variantId);
      } else if (requestedColor) {
        stateUrl.searchParams.set('color', requestedColor);
      }
      if (variantId) url.searchParams.set('variant', variantId);

      // Shared cache — a hover prefetch for this same colour URL (Liquid card or
      // React PLP) has already paid for this render, and a click that lands
      // mid-prefetch joins that request instead of issuing a second one.
      // `fetchQuickAddGrid` adds view/quick_add and returns a clone we own.
      const clone = await fetchQuickAddGrid(url.toString());
      if (!clone) {
        window.location.href = productUrl;
        return false;
      }

      // Start the availability fetch off the detached clone, before the DOM
      // work below — on a heavy product the transplant/replaceChildren/hydrate
      // sequence is tens of ms of main thread we'd otherwise spend before the
      // request even leaves. De-duped against the picker's own fetch on mount.
      warmQuickAddAvailability(clone);

      transplantQuickAddPowerReviewsPlaceholder(modalContent, clone);
      delete modalContent.dataset.quickAddEditLineItemKey;
      delete modalContent.dataset.quickAddLineItemProperties;
      delete modalContent.dataset.quickAddEditSellingPlanId;
      modalContent.classList.remove('quick-add-modal--edit-mode');
      modalContent.classList.add('quick-add-modal--hydrating');
      modalContent.setAttribute('data-product-url', stateUrl.toString());
      modalContent.replaceChildren(clone);
      hydrateQuickAddPowerReviewsSnippets(modalContent);
      scheduleQuickAddImplicitNonColorSelectionClear(modalContent);
      startPiv2QuickAddAvailability(modalContent);
      // Must run before `waitForQuickAddMediaReady` below. The piv2 grid arrives
      // with `data-media-ready="false"` and nothing on this path ever flipped it
      // — QuickAddComponent's copy is private to the Liquid-card path — so that
      // wait could only resolve via its own 1200ms timeout. That was a fixed
      // 1.2s of blurred `--hydrating` shimmer on every PLP quick shop, long
      // after the image had decoded, which the homepage path never paid.
      markQuickAddMediaReady(modalContent);

      // Open as soon as the markup is in. The availability round trip below can
      // take seconds on a heavy combined listing, and blocking the open on it
      // meant the shopper stared at the PLP with no feedback the whole time —
      // 7s on Jetsetter. The drawer now opens against SSR swatches under the
      // `--hydrating` shimmer (which also suppresses the "Pick a size" error
      // labels), and the correct swatch lands a moment later.
      //
      // The primary-image decode used to be awaited HERE, ahead of the open,
      // costing up to 180ms before the shopper saw anything. It runs after the
      // open now (matching QuickAddComponent#finishQuickAddHydration) — the
      // shimmer already covers an undecoded image, so there is nothing to gain
      // from holding the drawer shut for it.
      if (dialogComponent instanceof QuickAddDialog) {
        dialogComponent.showDialog();
        dialogOpened = true;
      } else {
        const dialog = dialogComponent?.querySelector?.('dialog');
        if (dialog && typeof dialog.showModal === 'function') {
          dialog.showModal();
          dialogOpened = true;
        } else {
          window.location.href = productUrl;
          return false;
        }
      }

      await waitForQuickAddPrimaryImage(modalContent);

      if (variantId) {
        if (modalContent.querySelector('variant-picker-v2')) {
          await waitForPiv2AvailabilityReady(modalContent);
        }
        await QuickAddComponent.syncVariantSelectionById(modalContent, variantId);
      } else {
        if (requestedColor) {
          // Wait for piv2 to hydrate before clicking the swatch, otherwise
          // #onValueSelected no-ops (picker not ready) and the SSR-rendered
          // swatch — which may be the default — is left showing.
          if (modalContent.querySelector('variant-picker-v2')) {
            await waitForPiv2AvailabilityReady(modalContent);
          }
          QuickAddComponent.syncColorSelectionByValue(modalContent, requestedColor);
        }
        scheduleQuickAddImplicitNonColorSelectionClear(modalContent);
      }

      await waitForQuickAddMediaReady(modalContent);
      scheduleQuickAddImplicitNonColorSelectionClear(modalContent);
      modalContent.classList.remove('quick-add-modal--hydrating');
      // Elevar dl_view_item — this vanilla quick-add IS the active quick view
      // (renders variant-picker-v2). Elevar's embed never sees it (no page load).
      // Fire-and-forget so it can't delay/break the modal.
      trackQuickAddViewItem(productUrl, variantId, requestedColor);
      return true;
    } catch (err) {
      console.error('[QuickAdd] Open error:', err);
      if (dialogOpened) {
        // Dialog is already visible — keep it open rather than navigating away.
        // Drop the hydration shimmer so the SSR swatches are usable; piv2's own
        // failOpen renders an interactive fallback.
        modalContent.classList.remove('quick-add-modal--hydrating');
        return false;
      }
      window.location.href = productUrl;
      return false;
    }
  }

  document.addEventListener('bonobos:open-quick-add', (event) => {
    const detail = event instanceof CustomEvent ? event.detail : null;
    const productUrl = detail?.productUrl;
    if (typeof productUrl !== 'string' || !productUrl) return;
    openQuickAddDialog(productUrl, {
      selectedColor: typeof detail?.selectedColor === 'string' ? detail.selectedColor : undefined,
    });
  });

  // Elevar dl_view_item on an in-drawer colour change. Selecting a colour in the
  // quick-add drawer swaps to the sibling (combined-listing) product with no page
  // load, so Elevar's embed never sees it and the open-time trackQuickAddViewItem
  // only ever covered the first colour. variant-picker-v2's #onValueSelected
  // dispatches a bubbling 'piv2:quick-add-sibling-hydrate' for EVERY context, so
  // filter to `quick-add` (the PDP soft-nav handler already covers 'pdp', and
  // firing there would double-count). Deduped on the target URL so a re-render's
  // repeat dispatch for the same colour doesn't re-fire. Fire-and-forget —
  // trackQuickAddViewItem resolves the swapped product's variant from its own
  // product JSON and never throws into the UI.
  let lastTrackedSiblingUrl = '';
  document.addEventListener('piv2:quick-add-sibling-hydrate', (event) => {
    const detail = event instanceof CustomEvent ? event.detail : null;
    if (!detail || detail.context !== 'quick-add') return;
    const productUrl = typeof detail.productUrl === 'string' ? detail.productUrl.trim() : '';
    if (!productUrl || productUrl === lastTrackedSiblingUrl) return;
    lastTrackedSiblingUrl = productUrl;
    const colorName = typeof detail.colorName === 'string' ? detail.colorName : undefined;
    trackQuickAddViewItem(productUrl, undefined, colorName);
  });

  document.addEventListener('click', async (e) => {
    const editWishlistButton = e.target instanceof Element ? e.target.closest('[data-wishlist-toggle]') : null;
    if (editWishlistButton instanceof HTMLElement) {
      const modalContent = document.getElementById('quick-add-modal-content');
      const isEditModal = Boolean(
        modalContent?.classList.contains('quick-add-modal--edit-mode') || modalContent?.dataset.quickAddEditLineItemKey
      );
      if (modalContent?.contains(editWishlistButton) && isEditModal) {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        handleEditWishlistClick(editWishlistButton, modalContent);
        return;
      }
    }

    const editBtn = e.target instanceof Element ? e.target.closest('[data-quick-add-edit]') : null;
    if (!editBtn || !(editBtn instanceof HTMLElement)) return;

    const productUrl = editBtn.dataset.productUrl?.trim();
    const lineItemKey = editBtn.dataset.lineItemKey?.trim();
    const variantId = editBtn.dataset.variantId?.trim();
    const lineItemQuantity = getCartEditLineItemQuantity(editBtn);
    if (!productUrl || !lineItemKey) return;

    e.preventDefault();
    e.stopPropagation();

    const dialogComponent = document.getElementById('quick-add-dialog');
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!dialogComponent || !modalContent) {
      window.location.href = productUrl;
      return;
    }

    try {
      const quickAddRequestUrl = new URL(productUrl, window.location.origin);
      quickAddRequestUrl.searchParams.set('view', 'quick-add');
      quickAddRequestUrl.searchParams.set('quick_add', '1');
      if (variantId) {
        quickAddRequestUrl.searchParams.set('variant', variantId);
      }
      const quickAddUrl = quickAddRequestUrl.toString();
      const response = await fetch(quickAddUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const gridElement = extractQuickAddProductGrid(html);
      if (!gridElement) {
        window.location.href = productUrl;
        return;
      }

      const clone = /** @type {Element} */ (gridElement.cloneNode(true));

      transplantQuickAddPowerReviewsPlaceholder(modalContent, clone);
      modalContent.classList.add('quick-add-modal--hydrating');
      // Set the cart-edit dataset BEFORE replaceChildren so the gift-card
      // recipient form's connectedCallback (which fires synchronously on
      // mount) can see we're in cart-edit context and pick the line-item-
      // scoped sessionStorage key on its initial restore.
      modalContent.dataset.quickAddEditLineItemKey = lineItemKey;
      const lineItemPropertiesRaw =
        editBtn.getAttribute('data-line-item-properties') || editBtn.dataset.lineItemProperties || '';
      modalContent.dataset.quickAddLineItemProperties = lineItemPropertiesRaw;
      const sellingPlanId = editBtn.dataset.sellingPlanId?.trim();
      if (sellingPlanId) {
        modalContent.dataset.quickAddEditSellingPlanId = sellingPlanId;
      } else {
        delete modalContent.dataset.quickAddEditSellingPlanId;
      }
      modalContent.replaceChildren(clone);
      modalContent.setAttribute('data-product-url', productUrl);
      hydrateQuickAddPowerReviewsSnippets(modalContent);
      startPiv2QuickAddAvailability(modalContent);
      modalContent.classList.add('quick-add-modal--edit-mode');
      bindEditWishlist(modalContent);
      requestAnimationFrame(() => bindEditWishlist(modalContent));
      setTimeout(() => bindEditWishlist(modalContent), 300);
      await hydrateGiftCardRecipientForm(modalContent, lineItemPropertiesRaw);
      await waitForPiv2AvailabilityReady(modalContent);
      if (lineItemQuantity) applyQuickAddEditLineQuantity(modalContent, lineItemQuantity);

      if (variantId) {
        const currentVariantInput = modalContent.querySelector('product-form-component [ref="variantId"]');
        const alreadyHydratedToVariant =
          currentVariantInput instanceof HTMLInputElement && currentVariantInput.value === variantId;

        if (!alreadyHydratedToVariant) {
          await QuickAddComponent.syncVariantSelectionById(modalContent, variantId);
        }
      }
      if (lineItemQuantity) applyQuickAddEditLineQuantity(modalContent, lineItemQuantity);
      bindEditWishlist(modalContent);
      requestAnimationFrame(() => bindEditWishlist(modalContent));

      await waitForQuickAddPrimaryImage(modalContent);
      dialogComponent.addEventListener(DialogCloseEvent.eventName, () => {
        delete modalContent.dataset.quickAddLineItemProperties;
        delete modalContent.dataset.quickAddEditLineItemQuantity;
        delete modalContent.dataset.quickAddEditSellingPlanId;
        modalContent.classList.remove('quick-add-modal--edit-mode');
        if (documentSubmitCapture) {
          window.removeEventListener('submit', documentSubmitCapture, true);
          documentSubmitCapture = null;
        }
      }, { once: true });

      documentSubmitCapture = handleDocumentSubmitCapture;
      window.addEventListener('submit', documentSubmitCapture, true);

      if (dialogComponent instanceof QuickAddDialog) {
        dialogComponent.showDialog();
      } else {
        const dialog = dialogComponent?.querySelector?.('dialog');
        if (dialog && typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          window.location.href = productUrl;
        }
      }
      bindEditWishlist(modalContent);
      requestAnimationFrame(() => bindEditWishlist(modalContent));

      await waitForQuickAddMediaReady(modalContent);
      modalContent.classList.remove('quick-add-modal--hydrating');
      bindEditWishlist(modalContent);
    } catch (err) {
      console.error('[QuickAdd] Edit open error:', err);
      window.location.href = productUrl;
    }
  }, true);
})();

/**
 * Shop-the-look-inline: swatch click updates card media and quick-add product URL.
 * Listens for clicks on .shop-the-look-inline__swatch-button and updates the card's
 * image, image link href, view link href, and quick-add data-product-url.
 */
(function () {
  /**
   * Combined-listing sibling colors live on their own products, and Liquid can only
   * reach them through all_products — which is capped per page render, so some
   * swatches ship without a data-variant-image-url. Those used to leave the card
   * showing the previously selected color's photo, which reads as the wrong color
   * rather than as a missing one. Resolve the image from the product's own .js
   * endpoint instead, cached per handle so a row of swatches costs one request each
   * at most.
   */
  const shopTheLookInlineImageCache = new Map();

  // Match the width Liquid renders into data-variant-image-url, otherwise the
  // .js endpoint hands back the full-resolution original.
  const SHOP_THE_LOOK_INLINE_IMAGE_WIDTH = 368;

  const sizeShopTheLookInlineImage = (url) => {
    if (!url) return null;
    const absolute = url.startsWith('//') ? `https:${url}` : url;
    try {
      const parsed = new URL(absolute, window.location.origin);
      parsed.searchParams.set('width', String(SHOP_THE_LOOK_INLINE_IMAGE_WIDTH));
      return parsed.toString();
    } catch {
      return url;
    }
  };

  const fetchShopTheLookInlineImage = (variantUrl) => {
    const path = String(variantUrl || '').split('?')[0];
    if (!path.includes('/products/')) return Promise.resolve(null);
    if (shopTheLookInlineImageCache.has(path)) return shopTheLookInlineImageCache.get(path);

    const pending = fetch(`${path}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => sizeShopTheLookInlineImage(product?.featured_image || product?.images?.[0]))
      .catch(() => null);

    shopTheLookInlineImageCache.set(path, pending);
    return pending;
  };

  const applyShopTheLookInlineSwatchState = (card, btn, options = {}) => {
    const { commitActive = true } = options;
    if (!(card instanceof HTMLElement) || !(btn instanceof HTMLElement) || !btn.dataset.variantUrl) return;
    const variantUrl = btn.dataset.variantUrl;
    const imageUrl = btn.dataset.variantImageUrl || null;

    const imageLink = card.querySelector('.shop-the-look-inline__image-link');
    if (imageLink instanceof HTMLAnchorElement) {
      imageLink.href = variantUrl;
    }

    const titleLink = card.querySelector('.shop-the-look-inline__title');
    if (titleLink instanceof HTMLAnchorElement) {
      titleLink.href = variantUrl;
    }

    const img = card.querySelector('.shop-the-look-inline__image');
    if (img instanceof HTMLImageElement) {
      // Token guards against a slow fetch landing after the pointer has already
      // moved on to another swatch.
      const token = String((Number(card.dataset.stlImageToken) || 0) + 1);
      card.dataset.stlImageToken = token;

      if (imageUrl) {
        img.src = imageUrl;
        img.srcset = imageUrl;
        img.decoding = 'async';
      } else {
        fetchShopTheLookInlineImage(variantUrl).then((resolvedUrl) => {
          if (!resolvedUrl || card.dataset.stlImageToken !== token) return;
          // Cache on the button so re-hovering this swatch is instant.
          btn.dataset.variantImageUrl = resolvedUrl;
          img.src = resolvedUrl;
          img.srcset = resolvedUrl;
          img.decoding = 'async';
        });
      }
    }

    const viewLink = card.querySelector('.shop-the-look-inline__view-link');
    if (viewLink instanceof HTMLAnchorElement) {
      viewLink.href = variantUrl;
    }

    const subtitle = card.querySelector('.shop-the-look-inline__subtitle');
    if (subtitle instanceof HTMLElement) {
      subtitle.textContent = btn.dataset.variantSubtitle || '';
      subtitle.hidden = !btn.dataset.variantSubtitle;
    }

    const priceContainer = card.querySelector('.shop-the-look-inline__price [ref="priceContainer"]');
    if (priceContainer instanceof HTMLElement && btn.dataset.priceHtml) {
      const nextPriceMarkup = document.createElement('div');
      nextPriceMarkup.innerHTML = btn.dataset.priceHtml;
      const nextPriceContainer = nextPriceMarkup.querySelector('[ref="priceContainer"]');
      if (nextPriceContainer instanceof HTMLElement) {
        priceContainer.replaceWith(nextPriceContainer);
      }
    }

    // Both the desktop and mobile "+N" badges are in the DOM (only one is
    // visible per breakpoint), so update every match, not just the first.
    card.querySelectorAll('.shop-the-look-inline__swatches-more').forEach((swatchesMore) => {
      if (!(swatchesMore instanceof HTMLAnchorElement)) return;
      swatchesMore.href = variantUrl;
      if (btn.dataset.variantId) swatchesMore.setAttribute('data-variant-id', btn.dataset.variantId);
      if (btn.dataset.variantUrl) swatchesMore.setAttribute('data-variant-url', btn.dataset.variantUrl);
    });

    const quickAdd = card.querySelector('quick-add-component');
    if (quickAdd && variantUrl) {
      quickAdd.setAttribute('data-product-url', variantUrl);
      if (btn.dataset.variantId) quickAdd.setAttribute('data-variant-id', btn.dataset.variantId);
      quickAdd.setAttribute('data-quick-add-source', 'shop-the-look');
    }

    if (commitActive) {
      card.querySelectorAll('.shop-the-look-inline__swatch-button').forEach((b) => {
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        b.classList.toggle('is-active', b === btn);
      });
    }
  };

  const restoreShopTheLookInlineActiveSwatchState = (card) => {
    if (!(card instanceof HTMLElement)) return;
    const activeButton = card.querySelector('.shop-the-look-inline__swatch-button.is-active');
    if (!(activeButton instanceof HTMLElement)) return;

    applyShopTheLookInlineSwatchState(card, activeButton, { commitActive: false });
  };

  document.addEventListener('click', (e) => {
    const target = e.target;
    const btn = target instanceof Element ? target.closest('.shop-the-look-inline__swatch-button') : null;
    if (!btn || !(btn instanceof HTMLElement) || !btn.dataset.variantUrl) return;

    const card = btn.closest('[data-shop-the-look-inline]');
    if (!card) return;

    e.preventDefault();
    applyShopTheLookInlineSwatchState(card, btn);
  });

  document.addEventListener('pointerover', (e) => {
    if (!(e instanceof PointerEvent) || e.pointerType !== 'mouse') return;
    const target = e.target;
    const btn = target instanceof Element ? target.closest('.shop-the-look-inline__swatch-button') : null;
    if (!btn || !(btn instanceof HTMLElement) || !btn.dataset.variantUrl) return;

    const card = btn.closest('[data-shop-the-look-inline]');
    if (!card) return;

    applyShopTheLookInlineSwatchState(card, btn, { commitActive: false });
  });

  document.addEventListener('pointerout', (e) => {
    if (!(e instanceof PointerEvent) || e.pointerType !== 'mouse') return;
    const target = e.target;
    const btn = target instanceof Element ? target.closest('.shop-the-look-inline__swatch-button') : null;
    if (!btn || !(btn instanceof HTMLElement)) return;

    const card = btn.closest('[data-shop-the-look-inline]');
    if (!card) return;

    const nextButton =
      e.relatedTarget instanceof Element ? e.relatedTarget.closest('.shop-the-look-inline__swatch-button') : null;
    if (nextButton instanceof HTMLElement && card.contains(nextButton)) return;

    restoreShopTheLookInlineActiveSwatchState(card);
  });

  document.addEventListener('focusin', (e) => {
    const target = e.target;
    const btn = target instanceof Element ? target.closest('.shop-the-look-inline__swatch-button') : null;
    if (!btn || !(btn instanceof HTMLElement) || !btn.dataset.variantUrl) return;

    const card = btn.closest('[data-shop-the-look-inline]');
    if (!card) return;

    applyShopTheLookInlineSwatchState(card, btn, { commitActive: false });
  });

  document.addEventListener('focusout', (e) => {
    const target = e.target;
    const btn = target instanceof Element ? target.closest('.shop-the-look-inline__swatch-button') : null;
    if (!btn || !(btn instanceof HTMLElement)) return;

    const card = btn.closest('[data-shop-the-look-inline]');
    if (!card) return;

    const nextButton =
      e.relatedTarget instanceof Element ? e.relatedTarget.closest('.shop-the-look-inline__swatch-button') : null;
    if (nextButton instanceof HTMLElement && card.contains(nextButton)) return;

    restoreShopTheLookInlineActiveSwatchState(card);
  });
})();
